// src/JudgeView.tsx
// ─────────────────────────────────────────────────────────────────────────────
//  Hlavní obrazovka rozhodčího
//  Route: /judge/:raceId
//
//  Zodpovídá za:
//   1. Ověření přihlášení
//   2. Nalezení stanovišť přiřazených tomuto rozhodčímu
//   3. Výpočet aktuální rotace (která runda je u mého stanoviště)
//   4. Renderování správné sub-obrazovky podle typu stanoviště
//   5. Posun na dalšího střelce / rundu po uložení výsledku
//
//  Zápisy výsledků podle fáze 1 živého bodování (docs/live-faze1-navrh.md):
//  vždy jen jedno stanoviště střelce (utils/liveResults), koncept střel se
//  maže až ve stejné dávce s přijatým výsledkem, neuložené výsledky hlídá
//  schránka „k odeslání“ (utils/liveOutbox, upozornění LiveOutboxNotice).
//
//  Fáze 3 (docs/live-faze3-navrh.md, 3a): závod, stanoviště, rundy
//  a startovní listina živě (useLiveRace, useStartList) – změny nastavení se
//  projeví hned; rozpracovaný střelec (od první rány) a nastavení stanoviště
//  pro zobrazeného střelce jsou zmrazené až do Potvrdit / Zrušit. Oprávnění
//  z utils/livePermissions (jeden divák z useAuth).
//
//  Fáze 3 (3b): dialogy a hlášky aplikace místo oken prohlížeče (I3 – dvojí
//  potvrzení Ukončit zůstává), nováček mimo tlačítko střelce (I5), odznak N
//  s tmavým textem (I7), procento postupu = uložené výsledky stanovišť ÷
//  očekávané (I8), čitelné výběry stanoviště a rundy na úzkém telefonu.
//  Bodování (ZÁSAH/NULA, gongy) beze změny.
//
//  Krok 4 – automatická rotace (docs/live-auto-rotace-navrh.md, rozbor §5):
//  kde je která runda, se ODVOZUJE z výsledků, rund, stanovišť a uvolnění
//  (utils/liveRotation) – žádný uložený průběh, žádný zápis pro rotaci kromě
//  výslovného uvolnění „Runda hotová – chybí #N“ / terč „odstřílela“
//  (live_progress/{stationId}_r{N}). Telefon bez potvrzené rundy má rundu
//  PŘEDVYBRANOU ve stejném výběru rundy jako u ručních rund; rozhodčí ji
//  potvrdí ťuknutím na prvního střelce (gongy se samy neotevřou) nebo ve
//  výběru zvolí jinou – jen pro svůj telefon. Potvrzená runda běží stejným
//  ručním tokem (řádek nad ZÁSAH/NULA beze změny), dokud není na stanovišti
//  podle dat hotová. Celý závod na ruční rundy přepne jen správce v nastavení
//  (dřívější zápis manualRotation z výběru rundy je pryč). Automatický je jen
//  závod s autoRotation === true a rotationStations (spuštění novou verzí);
//  starý příznak manualRotation: false = ruční rundy.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback, useMemo, useReducer, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  collection,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  serverTimestamp,
  waitForPendingWrites,
  where,
  getDocFromServer,
  runTransaction,
  type DocumentData,
} from "firebase/firestore";
import { useAuth } from "./utils/useAuth";
import { db } from "./firebase";
import {
  LiveStation,
  LiveRound,
  RoundShooter,
  JudgeShotsResult,
  LiveResult,
} from "./types/liveScoring";
import JudgeGongsSequential from "./JudgeGongsSequential";
import JudgeGongsChoice from "./JudgeGongsChoice";
import JudgeTarget from "./JudgeTarget";
import { useWakeLock } from "./utils/useWakeLock";
import {
  publishLiveResults,
  publishNoticeText,
  registrationNewbies,
  resolveNewbie,
  roundShooterWithStartList,
} from "./utils/publishResults";
import {
  safeShooterName,
  useStartList,
  type StartListRow,
} from "./utils/startList";
import {
  canDisableNewbie,
  canFinishRace as canFinishRaceOf,
  canPublishResults,
  canWriteStationResult,
  isHeadJudgeOf,
  isLiveJudgeOf,
  liveViewer,
} from "./utils/livePermissions";
import { useLiveRace } from "./utils/useLiveRace";
import {
  deriveRotation,
  frozenRotationOf,
  hasStationResult,
  markerDocId,
  markersOf,
  stationQueue,
  stopOf,
  suggestedRound,
  upcomingRounds,
  type RotationMarker,
} from "./utils/liveRotation";
import { RotationOverview } from "./judge/RotationOverview";
import { useContentStable } from "./judge/useContentStable";
import { disableNewbiePermanently, setStartListNewbie } from "./utils/liveNewbies";
import { useToast } from "./ui/Toast";
import { useConfirm } from "./ui/ConfirmDialog";
import { firestoreQueueDurable } from "./utils/firestoreCache";
import * as dualPicks from "./judge/dualPicks";
import { useIsMobile } from "./utils/useIsMobile";
import { useNewAppVersion } from "./utils/useNewAppVersion";
import { JUDGE_NAME_FALLBACK } from "./utils/shooterName";
import { reportError, reportWarning } from "./utils/reportError";
import { firebaseErrorMessage } from "./utils/firebaseErrors";
import {
  ensureLiveJudges,
  liveResultRef,
  liveSaveErrorMessage,
  hasLiveResult,
  loadLiveResultsFromServer,
  loadLiveRoundsFromServer,
  loadLiveStationsFromServer,
  removeStationResultAsJudge,
  saveStationResult,
} from "./utils/liveResults";
import { liveProgress, missingResults, missingResultsText } from "./utils/liveProgress";
import { changedLiveResults } from "./utils/liveRepublish";
import {
  notifyOutboxRejected,
  onOutboxRejected,
  outboxAdd,
  outboxDiscard,
  outboxList,
  outboxReconcile,
  outboxRemove,
  type LostEntry,
  type OutboxEntry,
} from "./utils/liveOutbox";
import LiveOutboxNotice from "./LiveOutboxNotice";

/** Záloha starších verzí, když rozhodčí neměl jméno ani e-mail. */
const LEGACY_JUDGE_NAME_FALLBACK = "Druhý rozhodčí";

/** Popisek stanoviště jako ve výběru stanoviště („3. Gongy“). */
function stationLabelOf(st: { number?: unknown; name?: unknown }): string {
  return [st.number, st.name].filter(v => v !== undefined && v !== null && v !== "").join(". ");
}

/**
 * Jak dlouho „Potvrdit“ čeká na potvrzení serveru, než rozhodčího pustí
 * k dalšímu střelci (zápis zůstane ve frontě a odešle se sám – fáze 3, D1).
 */
export const LIVE_SAVE_ACK_WAIT_MS = 1500;

/** Po jak dlouhé době posluchačů bez serveru (s připojením) ukázat „slabé spojení“ (D2). */
export const WEAK_SIGNAL_MS = 10000;

/**
 * Zápis zamítnutý pravidly: je stanoviště na serveru smazané (F1)? Jen
 * potvrzení serveru – bez spojení nebo s chybou čtení false (obecná hláška).
 */
async function stationDeletedOnServer(raceId: string, stationId: string): Promise<boolean> {
  try {
    const stations = await loadLiveStationsFromServer(raceId);
    return !stations.some(st => st.id === stationId);
  } catch {
    return false;
  }
}

/** Ukončení závodu, který mezitím ukončil někdo jiný (F2). */
export const RACE_ALREADY_FINISHED_MESSAGE =
  "Závod už ukončil jiný hlavní rozhodčí nebo správce – výsledky jsou zveřejněné.";
/** Ukončení závodu, který mezitím vrátili do přípravy. */
export const RACE_NOT_RUNNING_MESSAGE = "Závod mezitím neběží (vrácený do přípravy) – nic se neukončilo.";
/** Totéž, když se závod vrátil do přípravy během publikace (výsledky už zveřejněné). */
export const RACE_NOT_RUNNING_AFTER_PUBLISH_MESSAGE =
  "Závod mezitím neběží (vrácený do přípravy) – neukončil se, ale výsledky se už zveřejnily. Ověřte je v Živých výsledcích.";

/** Konkrétní zápis ze schránky (střelec, stanoviště, čas zadání). */
const outboxKeyOf = (e: OutboxEntry): string => `${e.startNumber}_${e.stationId}_${e.at}`;

/**
 * Střelec rundy, jak ho ukazuje obrazovka a zapisuje do hlavičky výsledku:
 * jméno ze startovní listiny (roundShooterWithStartList), nováček stejně
 * jako publikace (resolveNewbie – řádek listiny, přihláška, data rundy;
 * host bez účtu nováčkem není), zákaz nováčka z listiny i z rundy.
 */
function judgeShooter(
  shooter: RoundShooter,
  rows: ReadonlyMap<string, StartListRow>,
  registrations: ReadonlyMap<string, boolean> | null
): RoundShooter {
  const shown = roundShooterWithStartList(shooter, rows);
  const row = shooter.userId ? rows.get(shooter.userId) : undefined;
  const isNewbie =
    !!shooter.userId &&
    resolveNewbie(row, registrations ? registrations.get(shooter.userId) : undefined, {
      isNewbie: shown.isNewbie,
      newbieDisabled: shown.newbieDisabled,
    });
  return { ...shown, isNewbie, newbieDisabled: shown.newbieDisabled };
}

/** Nastavení stanoviště, které ovlivní bodování (zmrazení pro rozpracovaného střelce). */
function stationConfigKey(st: LiveStation | null | undefined): string {
  if (!st) return "";
  return JSON.stringify([st.type, st.shotsCount, st.timeLimit, !!st.dualShooter, st.config]);
}

/** Rozpracovaný střelec: od první rány do Potvrdit / Zrušit (návrh 3a, bod 5.2). */
interface ScoringLock {
  stationId: string;
  roundNumber: number;
  startNumber: number;
  /** Střelec při první ráně – záloha, když z rundy mezitím zmizel. */
  shooter: RoundShooter;
}

const TOAST_TOP_VAR = "--lks-toast-top";

/**
 * Toasty (ToastProvider v kořeni aplikace) se na obrazovce rozhodčího ukážou
 * pod horní lištou, ne přes ni (návrh 3.6). Vrací callback ref pro lištu:
 * --lks-toast-top na <html> = změřený spodní okraj lišty (lišta je pod
 * safe-area obalu Bare; ResizeObserver – na úzkém telefonu se může zalomit).
 * Bez lišty (ukončený závod, hlášky) a po odchodu z obrazovky platí původní
 * hodnota (jinde ji nastavuje AppShell). Jen callback ref, žádný efekt:
 * React ho volá s null při odpojení lišty i při odchodu z obrazovky
 * (StrictMode v Reactu 18 zdvojuje efekty, refy ne).
 */
function useToastTopBelow(): (el: HTMLElement | null) => void {
  const saved = useRef<{ value: string; priority: string } | null>(null);
  const observer = useRef<ResizeObserver | null>(null);

  return useCallback((el: HTMLElement | null) => {
    if (observer.current) observer.current.disconnect();
    observer.current = null;
    const style = document.documentElement.style;
    if (!el) {
      const prev = saved.current;
      saved.current = null;
      if (!prev) return;
      try {
        if (prev.value) style.setProperty(TOAST_TOP_VAR, prev.value, prev.priority);
        else style.removeProperty(TOAST_TOP_VAR);
      } catch {
        /* jen umístění hlášek – nesmí shodit obrazovku */
      }
      return;
    }
    if (!saved.current) {
      saved.current = {
        value: style.getPropertyValue(TOAST_TOP_VAR),
        priority: style.getPropertyPriority(TOAST_TOP_VAR),
      };
    }
    const update = () => {
      try {
        const bottom = Math.round(el.getBoundingClientRect().bottom);
        const h = bottom > 0 ? bottom : el.offsetHeight;
        if (h > 0) style.setProperty(TOAST_TOP_VAR, `${h}px`);
      } catch {
        /* jen umístění hlášek */
      }
    };
    update();
    if (typeof ResizeObserver !== "undefined") {
      observer.current = new ResizeObserver(update);
      observer.current.observe(el);
    }
  }, []);
}

// ─────────────────────────────────────────────────────────────
//  Super admin emaily (stejné jako v LiveRaceSetup)
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
//  Typy
// ─────────────────────────────────────────────────────────────

export interface RaceInfo {
  id: string;
  name: string;
  date: string;
  status: string;
  headJudgeUids?: string[];
  headJudgeUid?: string; // legacy
  /** Rozhodčí stanovišť závodu (sjednocení judgeUids, pravidla – návrh 3.1) */
  liveJudgeUids?: string[];
  /** Číslo rundy → startovní pozice v rotationStations (zmrazené při spuštění). */
  rotation?: { [roundNumber: string]: number };
  totalStations: number;
  totalRounds: number;
  startStationForRound1: number;
  /** Starší verze: ruční rundy (spuštění ho píše vždy true – kompatibilita). */
  manualRotation?: boolean;
  /** Automatická rotace (krok 4) – jen se zmrazeným rotationStations. */
  autoRotation?: boolean;
  /** Id stanovišť podle čísla při spuštění (automatická rotace). */
  rotationStations?: string[];
  livePublic?: boolean;
}

// ─────────────────────────────────────────────────────────────
//  Finalizace závodu – zápis do results_entries + results_stages
//  → přesunuto do utils/publishResults.ts (publishLiveResults),
//    sdíleno s AdminLiveResults (ruční re-publikace, opravy).
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
//  Komponenta
// ─────────────────────────────────────────────────────────────

const JudgeView: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();

  // ── Přihlášení: jeden divák z useAuth (B1) ────────────────
  // Správce = role admin/superadmin nebo trvalý super admin (jako isStaff
  // v pravidlech). Dřív vlastní onAuthStateChanged a checkSuperAdmin – pro
  // superadmina se závod načítal dvakrát.
  const authState = useAuth();
  const authUser = authState.user;
  const uid = authUser ? authUser.uid : null;
  const authLoading = !authState.ready;
  const viewer = useMemo(
    () => liveViewer({ user: authUser, role: authState.role, isSuperAdmin: authState.isSuperAdmin }),
    [authUser, authState.role, authState.isSuperAdmin]
  );
  const isStaff = viewer.isStaff;
  const { showToast } = useToast();
  // Dialogy aplikace místo window.confirm (I3) – některé WebView okna
  // prohlížeče potlačí
  const confirm = useConfirm();

  // ── Živá data závodu (A1) ──────────────────────────────────
  // Závod, stanoviště a rundy z posluchačů (stanoviště a rundy jen za běhu
  // závodu); stav závodu je vždy celý snímek – smazané pole (vrácení do
  // přípravy smaže status) zmizí i tady.
  const live = useLiveRace(raceId, { enabled: authState.ready && !!uid, setupOnlyWhenActive: true });
  const race = live.race as RaceInfo | null;
  // Závod běží – jen tehdy se čtou stanoviště, rundy a živé bodování
  // (ukončený nebo nespuštěný závod má vlastní obrazovku)
  const raceActive = race?.status === "active";
  // Veřejná startovní listina (jména, nováčci) – živě, změna hlavního
  // rozhodčího na jiném zařízení se projeví hned
  const startList = useStartList(raceActive && uid ? raceId : null);
  const isRaceHeadJudge = isHeadJudgeOf(race, uid);
  const isStaffOrHeadJudge = isStaff || isRaceHeadJudge;

  // Nováčci z přihlášek (jako publikace, E2) – přihlášky čte jen správce
  // a hlavní rozhodčí svého závodu; ostatní: řádek listiny, pak data rundy
  const [regNewbies, setRegNewbies] = useState<Map<string, boolean> | null>(null);
  useEffect(() => {
    setRegNewbies(null);
    if (!raceId || !raceActive || !isStaffOrHeadJudge) return;
    let cancelled = false;
    getDocs(query(collection(db, "registrations"), where("raceId", "==", raceId)))
      .then(snap => {
        if (!cancelled) setRegNewbies(registrationNewbies(snap.docs.map(d => ({ id: d.id, data: d.data() }))));
      })
      .catch(err => reportWarning("JudgeView.registrations", err, { raceId }));
    return () => { cancelled = true; };
  }, [raceId, raceActive, isStaffOrHeadJudge]);

  // Stanoviště (id jako u čtenářů, docId pro zápisy) a rundy se jmény ze
  // startovní listiny – stabilní podle obsahu (snímek jen s metadaty nic
  // nepřepočítá)
  const allStations: LiveStation[] = useContentStable(live.stations);
  const liveRounds = useContentStable(live.rounds);
  const startRows = startList.rows;
  const enrichedRounds = useMemo<LiveRound[]>(
    () => liveRounds.map(r => ({
      id: r.docId,
      number: r.number,
      shooters: r.shooters.map(s => judgeShooter(s, startRows, regNewbies)),
    })),
    [liveRounds, startRows, regNewbies]
  );
  const rounds = useContentStable(enrichedRounds);

  const [liveResults, setLiveResults] = useState<{ [startNum: number]: LiveResult }>({});
  // Uvolnění rund automatické rotace (live_progress/{stationId}_r{N}, krok 4)
  const [markers, setMarkers] = useState<RotationMarker[]>([]);
  // První snímek výsledků a uvolnění dorazil (i z mezipaměti) – do té doby
  // automatická rotace nic nenavrhuje (návrh z prázdných dat by se hysterezí
  // a předvýběrem terče udržel i po načtení)
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [markersLoaded, setMarkersLoaded] = useState(false);

  // Stanoviště tohoto uživatele (B3, B6): správce a hlavní rozhodčí všechna;
  // rozhodčí stanoviště svá, a je-li v seznamu rozhodčích závodu
  // (liveJudgeUids – pravidla mu dovolí zapsat kamkoli, rozhodnutí 1), i
  // ostatní jako „Zastoupit jiné stanoviště“
  const isLiveJudge = isLiveJudgeOf(race, uid);
  const ownStations = useMemo(
    () => (isStaffOrHeadJudge ? allStations : allStations.filter(s => (s.judgeUids || []).includes(uid || ""))),
    [allStations, isStaffOrHeadJudge, uid]
  );
  const coverStations = useMemo(
    () => (!isStaffOrHeadJudge && isLiveJudge ? allStations.filter(s => !ownStations.includes(s)) : []),
    [allStations, ownStations, isStaffOrHeadJudge, isLiveJudge]
  );
  const myStations = useMemo(() => [...ownStations, ...coverStations], [ownStations, coverStations]);

  // Načítání: přihlášení, závod, za běhu i stanoviště, rundy a startovní listina
  const loading = authLoading || (!!uid && (live.status === "loading" || (raceActive && !startList.ready)));
  const setupReady = raceActive && live.status === "ready" && startList.ready;
  const error = live.status === "error"
    ? `Chyba načítání: ${(live.error as { message?: string } | null)?.message || String(live.error)}`
    : live.status === "notFound" ? "Závod nenalezen." : "";

  // ── Vybrané stanoviště (pokud má rozhodčí víc) ────────────
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  // Pro manuální rotaci – rozhodčí si zvolí rundu
  const [manualRoundNumber, setManualRoundNumber] = useState<number | null>(null);
  // V manuálním módu sledujeme index střelce lokálně (nemáme live_progress)
  const [manualShooterIndex, setManualShooterIndex] = useState(0);
  // Manuálně vybraný střelec (startNumber) – null = zobraz picker
  const [pickedShooterStartNum, setPickedShooterStartNum] = useState<number | null>(null);
  // Automatická rotace: rozbalený seznam „Mimo pořadí“
  const [showOffRoster, setShowOffRoster] = useState(false);
  // Draft shots: klíč = "{stationId}_{startNumber}", hodnota = zadané rány
  const [draftMap, setDraftMap] = useState<{ [key: string]: number[] }>({});
  // Rozpracovaný střelec (od první rány): mění ho jen Potvrdit, Zrušit,
  // Uvolnit nebo výběr jiného stanoviště / rundy – ne změna dat (pozdní
  // zamítnutí jiného zápisu, auto-výběr posledního střelce, převzetí rundy)
  const [scoringLock, setScoringLock] = useState<ScoringLock | null>(null);
  const scoringLockRef = useRef(scoringLock);
  scoringLockRef.current = scoringLock;
  // Nastavení stanoviště pro zobrazeného střelce ("{stationId}_{startNumber}")
  // – zmrazené od prvního zobrazení gongů, nové platí od dalšího střelce
  const frozenRef = useRef<{ key: string; station: LiveStation } | null>(null);
  // Vybrané stanoviště zmizelo (smazané v nastavení / rozhodčí odebraný)
  const [stationNotice, setStationNotice] = useState<string | null>(null);
  // Id živých stanovišť – koncept smazaného stanoviště se už nezapisuje
  // (W3 jeho koncepty smazal; zapsaný by jen zvedl počítadlo id)
  const stationIdsRef = useRef<Set<string>>(new Set());
  stationIdsRef.current = new Set(allStations.map(s => s.id));

  // ── Automatická rotace (krok 4): odvozená poloha rund ─────
  // Zmrazená rotace podle obsahu polí (snímek závodu s metadaty má pokaždé
  // nový objekt); null = ruční rundy (i starý příznak manualRotation: false
  // a závod spuštěný starší verzí bez rotationStations)
  const rotationFieldsKey = JSON.stringify([
    race ? race.autoRotation === true : false,
    race && Array.isArray(race.rotationStations) ? race.rotationStations : null,
    race && race.rotation && typeof race.rotation === "object" ? race.rotation : null,
  ]);
  const frozenRotation = useMemo(() => {
    const [autoRotation, rotationStations, rotation] = JSON.parse(rotationFieldsKey);
    return frozenRotationOf({ autoRotation, rotationStations, rotation });
  }, [rotationFieldsKey]);
  const autoMode = frozenRotation !== null;
  // Kde je která runda – jen z dat, která obrazovka zobrazuje (vlastní
  // výsledky ve frontě bez signálu taky); nic se pro rotaci nezapisuje
  const rotationReady = resultsLoaded && markersLoaded;
  const rotationState = useMemo(
    () => (frozenRotation && rotationReady
      ? deriveRotation({ frozen: frozenRotation, stationIds: allStations.map(s => s.id), rounds, results: liveResults, markers })
      : null),
    [frozenRotation, rotationReady, allStations, rounds, liveResults, markers]
  );
  // Rozpracovaný střelec na vybraném stanovišti – jeho runda má přednost
  const lockRoundHere = scoringLock && scoringLock.stationId === selectedStationId ? scoringLock.roundNumber : null;
  const selectedLiveStation = selectedStationId ? allStations.find(s => s.id === selectedStationId) || null : null;
  // Návrh rundy (hystereze: předchozí návrh zůstane, dokud je runda pořád
  // na stanovišti – změna pořadí jinde ho pod rukou rozhodčího nepřehodí)
  const suggestionRef = useRef<{ stationId: string; round: number | null } | null>(null);
  const prevSuggestion = suggestionRef.current && suggestionRef.current.stationId === selectedStationId ? suggestionRef.current.round : null;
  const suggested = rotationState && selectedStationId ? suggestedRound(rotationState, selectedStationId, prevSuggestion) : null;
  if (rotationState) suggestionRef.current = selectedStationId ? { stationId: selectedStationId, round: suggested } : null;
  // Runda na obrazovce gongů: rozpracovaný střelec → potvrzená (ťuknutím na
  // střelce nebo výběrem) → návrh automatické rotace. Terč má vlastní výběr.
  const roundShownNo: number | null =
    lockRoundHere ?? manualRoundNumber ?? (autoMode && selectedLiveStation && selectedLiveStation.type !== "target" ? suggested : null);

  const saveDraft = useCallback((stationId: string, startNumber: number, shots: number[]) => {
    setDraftMap(prev => ({ ...prev, [`${stationId}_${startNumber}`]: shots }));
    if (!stationIdsRef.current.has(stationId) || !raceId) return;
    // Persistovat do Firestore pro případ převzetí a obnovení aplikace;
    // vrácení na 0 ran koncept smaže (D4 – jinak by po obnovení ožil)
    const ref = doc(db, `races/${raceId}/live_drafts`, `${stationId}_${startNumber}`);
    const write = shots.length > 0
      ? setDoc(ref, { shots, stationId, startNumber, updatedAt: Date.now() })
      : deleteDoc(ref);
    write.catch(err => reportWarning("JudgeView.draft", err, { raceId, stationId, startNumber }));
  }, [raceId]);

  // Koncept v paměti po přijatém výsledku. Koncept ve Firestore smaže až dávka
  // s výsledkem (návrh 3.2) – dřív se mazal předem a zamítnutý výsledek ho
  // ztratil.
  const dropDraftLocal = useCallback((stationId: string, startNumber: number) => {
    setDraftMap(prev => {
      const key = `${stationId}_${startNumber}`;
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // ── Neuložené výsledky (návrh 3.6) ─────────────────────────
  // Záznam ve schránce „k odeslání“ zmizí jen potvrzením serveru, novějším
  // zadáním téhož střelce a stanoviště nebo přes „Rozumím“ – zamítnutí ho
  // nesmaže (hláška u výsledku zmizí s obrazovkou, upozornění ne).
  // Obrazovka je otevřená (zamítnutí po odchodu ohlásí schránka obrazovce
  // otevřené znovu, jinak ho ukáže při dalším otevření)
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // Zamítnutí za otevřené obrazovky: hláška u výsledku podle
  // "{stationId}_{startNumber}" – přežije přemountování gongů (u posledního
  // střelce rundy se během ukládání ukáže „Runda hotová“)
  const [saveErrors, setSaveErrors] = useState<{ [key: string]: string }>({});
  // Výsledky ze schránky, které na serveru nejsou – upozornění s „Rozumím“
  // (ukazují se jen ty, které ve schránce pořád jsou)
  const [lostEntries, setLostEntries] = useState<LostEntry[]>([]);
  // Server zápis potvrdil – překreslit: znovu uložený výsledek z upozornění zmizí
  const [, outboxSettled] = useReducer((n: number) => n + 1, 0);
  // Popisky stanovišť pro upozornění (u ukončeného závodu se stanoviště nenačítají)
  const [noticeStations, setNoticeStations] = useState<{ [stationId: string]: string }>({});
  const reconcilingRef = useRef(false);
  const reconcileAgainRef = useRef(false);
  // Relace (závod|rozhodčí), pro kterou čeká opakování porovnání po odeslání
  // fronty – nejvýš jedno (waitForPendingWrites se ve vedlejší záložce
  // nemusí vyřešit nikdy, další by se jen hromadila)
  const followUpRef = useRef<string | null>(null);
  // Aktuální závod a rozhodčí – výsledek porovnání pro předchozí se zahodí
  const sessionRef = useRef("");
  sessionRef.current = `${raceId}|${uid}`;

  // Jiný závod nebo jiný rozhodčí (sdílené zařízení) – nic z předchozího
  useEffect(() => {
    setLostEntries(prev => (prev.length ? [] : prev));
    setSaveErrors(prev => (Object.keys(prev).length ? {} : prev));
    setNoticeStations(prev => (Object.keys(prev).length ? {} : prev));
  }, [raceId, uid]);

  /**
   * Porovná schránku se serverem (po odeslání fronty zápisů) a neuložené
   * výsledky přidá do upozornění. Zmizí přes „Rozumím“ nebo opětovným
   * uložením (chyba čtení vrací prázdný seznam – upozornění tím nezmizí).
   * Spouští se po otevření obrazovky online a po obnovení připojení, po každém
   * zamítnutém zápisu (gongy i terče – i z obrazovky, která mezitím zmizela)
   * a když závod přestane běžet. Když se fronta do limitu neodeslala (slabý
   * signál), porovná se znovu, jakmile odejde.
   */
  const reconcileLatestRef = useRef<(() => Promise<void>) | null>(null);
  const reconcileOutbox = useCallback(async () => {
    // Po odchodu z obrazovky nic – záznamy počkají na její další otevření
    if (!raceId || !uid || !mountedRef.current) return;
    // Běží-li porovnání, zopakuje se po něm (s aktuálním závodem a rozhodčím)
    if (reconcilingRef.current) { reconcileAgainRef.current = true; return; }
    reconcilingRef.current = true;
    reconcileAgainRef.current = false;
    const session = `${raceId}|${uid}`;
    try {
      const { lost, timedOut } = await outboxReconcile(raceId, uid);
      if (!mountedRef.current || sessionRef.current !== session) return;
      // Fronta se do limitu neodeslala – nic se neporovnalo. Zamítnutí, které
      // přijde potom a na jehož zápis nikdo nečeká (zápis z minulé relace),
      // by jinak zůstalo neohlášené do další události: jedno porovnání navíc,
      // až fronta odejde. Ve vedlejší záložce se nemusí vyřešit nikdy – nic
      // nevisí ani se neopakuje.
      if (timedOut && followUpRef.current !== session) {
        followUpRef.current = session;
        const settle = () => {
          if (followUpRef.current === session) followUpRef.current = null;
        };
        waitForPendingWrites(db).then(
          () => {
            settle();
            if (mountedRef.current && sessionRef.current === session) void reconcileLatestRef.current?.();
          },
          settle
        );
      }
      // Mezitím potvrzené, znovu zadané nebo potvrzené „Rozumím“ už ve schránce nejsou
      const pending = outboxList(raceId, uid);
      const still = lost.filter(e => pending.some(p => outboxKeyOf(p) === outboxKeyOf(e)));
      if (still.length === 0) return;
      setLostEntries(prev => {
        const map = new Map(prev.map(e => [outboxKeyOf(e), e] as [string, LostEntry]));
        still.forEach(e => map.set(outboxKeyOf(e), e));
        return Array.from(map.values()).sort((a, b) =>
          a.startNumber - b.startNumber || a.stationId.localeCompare(b.stationId));
      });
      getDocs(collection(db, `races/${raceId}/live_stations`))
        .then(snap => {
          const labels: { [stationId: string]: string } = {};
          snap.docs.forEach(d => {
            // Uložené id stanoviště má přednost (klíč výsledků)
            const st = { id: d.id, ...(d.data() as any) };
            labels[st.id] = stationLabelOf(st);
          });
          if (mountedRef.current && sessionRef.current === session) setNoticeStations(labels);
        })
        .catch(err => reportWarning("JudgeView.outboxStations", err, { raceId }));
    } finally {
      reconcilingRef.current = false;
      if (reconcileAgainRef.current && mountedRef.current) {
        reconcileAgainRef.current = false;
        void reconcileLatestRef.current?.();
      }
    }
  }, [raceId, uid]);
  reconcileLatestRef.current = reconcileOutbox;

  // Zápis zamítnut (gongy i terče, i po odchodu terčů z obrazovky): záznam
  // zůstal ve schránce – hned ho porovnat, upozornění ho ukáže, jakmile hláška
  // u výsledku není vidět
  const onSaveRejected = useCallback(() => {
    void reconcileOutbox();
  }, [reconcileOutbox]);

  // Zamítnutí ohlášené přes schránku (notifyOutboxRejected) – i zápisu
  // z obrazovky, která mezitím zmizela (rozhodčí během „Ukládám…“ odešel
  // šipkou zpět a obrazovku otevřel znovu; porovnání staré obrazovky už
  // neběží). Zápis z této obrazovky ohlásí i onSaveRejected – druhé spuštění
  // se sloučí s běžícím porovnáním (nejvýš jedno opakování po něm).
  useEffect(() => onOutboxRejected(() => {
    void reconcileLatestRef.current?.();
  }), []);

  // „Rozumím“ – rozhodčí neuložené výsledky nahlásí hlavnímu rozhodčímu (jen
  // ty, které upozornění ukazuje – výsledek s hláškou u gongů zůstane)
  const acknowledgeLost = useCallback((shown: LostEntry[]) => {
    outboxDiscard(shown);
    const done = new Set(shown.map(outboxKeyOf));
    setLostEntries(prev => prev.filter(e => !done.has(outboxKeyOf(e))));
  }, []);


  // ── DUAL MODE stav ────────────────────────────────────────
  // Firestore: races/{raceId}/live_dual_roles/{stationId}_{roundNum}
  // Dokument: { activePicks: { [startNumber: string]: string (uid) } }
  // Každý rozhodčí si vybere libovolného střelce ze seznamu dostupných.
  // Poslední střelec zadaný tímto rozhodčím (pro opravu)
  const [dualLastEnteredStartNum, setDualLastEnteredStartNum] = useState<number | null>(null);
  // activePicks z Firestore: startNum → uid kdo ho má
  const [dualActivePicks, setDualActivePicks] = useState<Record<string, string>>({});

  // Helper: klíč dokumentu pro dual role – runda na obrazovce (rozpracovaný
  // střelec má přednost; v automatické rotaci i navržená runda, rezervace
  // ji potvrdí – dvojice se uprostřed rundy nepřepne)
  const dualRoleKey = React.useMemo(() =>
    selectedStationId && roundShownNo != null
      ? `${selectedStationId}_${roundShownNo}`
      : null,
    [selectedStationId, roundShownNo]
  );

  // Startovní číslo které mám aktuálně já vybráno (nebo null)
  const myDualPickedSN: number | null = React.useMemo(() => {
    if (!uid) return null;
    const entry = Object.entries(dualActivePicks).find(([, u]) => u === uid);
    return entry ? parseInt(entry[0]) : null;
  }, [dualActivePicks, uid]);

  // Poslední známé rezervace podle klíče (automatická rotace: vlastní
  // rezervace v dokumentu rundy, kterou obrazovka opustila, se uvolní)
  const dualSnapshotRef = useRef<{ key: string; picks: Record<string, string> } | null>(null);

  // Automatická rotace: obrazovka opustila rundu (data se smazala, runda
  // zmizela, Zrušit), ve které měl tento telefon rezervaci → uvolnit ji
  // podle posledního snímku (jinak by druhému rozhodčímu střelce držela).
  // Ruční rundy beze změny. Před posluchačem (efekty běží v pořadí) – snímek
  // nového klíče předchozí ještě nepřepsal.
  useEffect(() => {
    const last = dualSnapshotRef.current;
    if (!last || last.key === dualRoleKey) return;
    dualSnapshotRef.current = null;
    if (autoMode && raceId && uid && Object.values(last.picks).includes(uid)) {
      dualPicks.releaseDualPick(raceId, last.key, uid, last.picks);
    }
  }, [autoMode, raceId, uid, dualRoleKey]);

  // onSnapshot na dual picks dokument; jiná runda / stanoviště nikdy
  // nepoužije rezervace předchozí (do prvního snímku prázdné)
  useEffect(() => {
    setDualActivePicks({});
    if (!raceId || !dualRoleKey) return;
    const ref = doc(db, `races/${raceId}/live_dual_roles`, dualRoleKey);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const picks = snap.exists() ? ((snap.data() as { activePicks?: Record<string, string> }).activePicks || {}) : {};
        dualSnapshotRef.current = { key: dualRoleKey, picks };
        setDualActivePicks(picks);
      },
      (err) => reportWarning("JudgeView.dualPicks", err, { raceId })
    );
    return unsub;
  }, [raceId, dualRoleKey]);

  // Notifikace – někdo mi převzal střelce
  const [takeoverNotif, setTakeoverNotif] = useState<{ sn: number; name: string; by: string } | null>(null);
  useEffect(() => {
    if (!raceId || !dualRoleKey || !uid) return;
    const notifRef = doc(db, `races/${raceId}/live_dual_roles`, `${dualRoleKey}_notif`);
    // Skrytí po 8 s – jen poslední upozornění, po odchodu nic (D5)
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const unsub = onSnapshot(
      notifRef,
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() as any;
        if (d.forUid === uid && d.takenAt && Date.now() - d.takenAt < 30000) {
          // Starší verze psaly místo jména rozhodčího e-mail – ten se neukáže;
          // obecné jméno → „převzat druhým rozhodčím"
          const by = safeShooterName(d.takenBy);
          setTakeoverNotif({
            sn: d.takenSN,
            name: safeShooterName(d.takenName),
            by: by === JUDGE_NAME_FALLBACK || by === LEGACY_JUDGE_NAME_FALLBACK ? "" : by,
          });
          if (hideTimer) clearTimeout(hideTimer);
          hideTimer = setTimeout(() => setTakeoverNotif(null), 8000);
        }
      },
      (err) => reportWarning("JudgeView.dualNotif", err, { raceId })
    );
    return () => {
      unsub();
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [raceId, dualRoleKey, uid]);

  // Rezervace střelce, převzetí a uvolnění (judge/dualPicks, D3): se
  // spojením transakcí, bez něj do fronty s varováním (rozhodnutí 7′)
  const [dualOffline, setDualOffline] = useState(false);
  // Rezervace / převzetí právě běží (startovní číslo): další ťuknutí nic
  // nedělá, ťuknuté tlačítko ukáže kolečko
  const [dualBusySN, setDualBusySN] = useState<number | null>(null);
  const dualPicksRef = useRef(dualActivePicks);
  dualPicksRef.current = dualActivePicks;
  const dualRoleKeyRef = useRef(dualRoleKey);
  dualRoleKeyRef.current = dualRoleKey;
  useEffect(() => {
    setDualOffline(false);
  }, [dualRoleKey]);

  /**
   * Pozdní zpráva rezervace bez spojení: zápis ve frontě potvrzený nebo
   * pozdní transakce prošla → pás zmizí; zamítnutý zápis → pás zmizí
   * a hláška; transakce na cestě zjistila obsazeného střelce → hláška.
   */
  const onDualLate = useCallback((roleKey: string, startNumber: number) => (late: dualPicks.LateOutcome) => {
    if (!mountedRef.current) return;
    const current = dualRoleKeyRef.current === roleKey;
    if (late === "taken") {
      showToast(`Střelce #${startNumber} si mezitím vzal druhý rozhodčí – vyberte jiného.`, "warning");
      return;
    }
    if (current) setDualOffline(false);
    if (late === "rejected") showToast("Rezervace střelce se neuložila – vyberte ho znovu.", "error");
  }, [showToast]);

  const pickDualShooter = useCallback(async (startNumber: number) => {
    if (!raceId || !dualRoleKey || !uid || dualBusySN !== null) return;
    setDualBusySN(startNumber);
    try {
      const outcome = await dualPicks.pickDualShooter(
        raceId, dualRoleKey, uid, startNumber, dualPicksRef.current, onDualLate(dualRoleKey, startNumber)
      );
      if (outcome === "taken") {
        setPickedShooterStartNum(null);
        showToast(`Střelce #${startNumber} si mezitím vzal druhý rozhodčí – vyberte jiného.`, "warning");
      }
      setDualOffline(outcome === "queued");
    } catch (e) {
      reportError("JudgeView.pickDualShooter", e, { raceId });
      showToast(`Střelce #${startNumber} se nepodařilo rezervovat: ${firebaseErrorMessage(e)}`, "error");
    } finally {
      if (mountedRef.current) setDualBusySN(null);
    }
  }, [raceId, dualRoleKey, uid, dualBusySN, onDualLate, showToast]);

  // Převzít střelce od partnera (force override); rozepsané rány z konceptu
  const takeoverDualShooter = useCallback(async (startNumber: number, shooterName: string) => {
    if (!raceId || !dualRoleKey || !uid || !selectedStationId || dualBusySN !== null) return false;
    setDualBusySN(startNumber);
    try {
      const { outcome, shots } = await dualPicks.takeoverDualShooter(
        raceId, dualRoleKey, selectedStationId, uid, startNumber, shooterName, dualPicksRef.current,
        onDualLate(dualRoleKey, startNumber)
      );
      if (shots.length > 0) setDraftMap(prev => ({ ...prev, [`${selectedStationId}_${startNumber}`]: shots }));
      setDualOffline(outcome === "queued");
      return true;
    } catch (e) {
      reportError("JudgeView.takeoverDualShooter", e, { raceId });
      showToast(`Střelce #${startNumber} se nepodařilo převzít: ${firebaseErrorMessage(e)}`, "error");
      return false;
    } finally {
      if (mountedRef.current) setDualBusySN(null);
    }
  }, [raceId, dualRoleKey, uid, selectedStationId, dualBusySN, onDualLate, showToast]);

  /** Kolečko v ťuknutém tlačítku rezervace (přes obsah – bez posunu rozložení). */
  const dualBusySpinner = (startNumber: number) =>
    dualBusySN === startNumber ? (
      <span
        className="spinner-border spinner-border-sm"
        role="status"
        aria-label="Rezervuji…"
        style={{ position: "absolute", top: "0.3rem", right: "0.3rem", width: "0.8rem", height: "0.8rem" }}
      />
    ) : null;

  // Uvolnit svůj pick (po uložení výsledku, Uvolnit, Změnit roli) – zápis
  // po polích do fronty, nikdo na něj nečeká
  const releaseDualPick = useCallback(() => {
    if (!raceId || !dualRoleKey || !uid) return;
    dualPicks.releaseDualPick(raceId, dualRoleKey, uid, dualPicksRef.current);
  }, [raceId, dualRoleKey, uid]);

  // Zabraňuje uspání displeje po dobu rozhodčování
  useWakeLock(!authLoading && !!uid && setupReady);

  // Výběr stanoviště, rundy a střelce zrušit (rozpracovaný střelec taky)
  const clearSelection = useCallback(() => {
    setSelectedStationId(null);
    setManualRoundNumber(null);
    setManualShooterIndex(0);
    setPickedShooterStartNum(null);
    setScoringLock(null);
  }, []);

  // Popisek naposledy vybraného stanoviště – pro hlášku, když zmizí
  const lastStationLabelRef = useRef("");

  // Vybrané stanoviště zmizelo (smazané v nastavení, rozhodčí odebraný bez
  // práva zastoupit) → zpět na výběr s hláškou
  useEffect(() => {
    if (!setupReady) return;
    if (selectedStationId && !myStations.some(s => s.id === selectedStationId)) {
      const label = lastStationLabelRef.current || selectedStationId;
      setStationNotice(
        allStations.some(s => s.id === selectedStationId)
          ? `Stanoviště ${label} vám už není přiřazené – vyberte jiné.`
          : `Stanoviště ${label} bylo v nastavení smazáno – vyberte jiné.`
      );
      clearSelection();
      return;
    }
    // Jediné vlastní stanoviště se vybere samo – ne hned po zmizení vybraného
    // (rozhodčí si stanoviště zvolí vědomě)
    if (!selectedStationId && ownStations.length === 1 && !stationNotice) setSelectedStationId(ownStations[0].id);
  }, [setupReady, selectedStationId, myStations, ownStations, allStations, clearSelection, stationNotice]);

  // Runda smazaná v nastavení → zpět na výběr rundy (i rozpracovaný střelec)
  useEffect(() => {
    if (!setupReady) return;
    const lockedRound = scoringLock ? scoringLock.roundNumber : null;
    if (lockedRound !== null && !rounds.some(r => r.number === lockedRound)) setScoringLock(null);
    if (manualRoundNumber !== null && !rounds.some(r => r.number === manualRoundNumber)) {
      setManualRoundNumber(null);
      setManualShooterIndex(0);
      setPickedShooterStartNum(null);
      setScoringLock(null);
    }
  }, [setupReady, rounds, manualRoundNumber, scoringLock]);

  // Závod přestal běžet (ukončen, vrácen do přípravy): výběr i koncepty
  // v paměti pryč – po dalším spuštění se začíná od výběru stanoviště
  // (koncepty ve Firestore zůstanou)
  useEffect(() => {
    if (raceActive) return;
    clearSelection();
    setDraftMap(prev => (Object.keys(prev).length ? {} : prev));
    setDualLastEnteredStartNum(null);
    frozenRef.current = null;
  }, [raceActive, clearSelection]);

  // Automatická rotace přepnutá za běhu na ruční (W6, jen správce):
  // potvrzená runda zůstane vybraná jako ruční, rozpracovaný střelec taky
  // (jeho runda se vybere); navržená (nepotvrzená) runda zmizí – ruční výběr
  const prevAutoRef = useRef<boolean | null>(null);
  const raceAuto = race ? autoMode : null;
  useEffect(() => {
    const prev = prevAutoRef.current;
    prevAutoRef.current = raceAuto;
    if (prev === true && raceAuto === false) {
      const lock = scoringLockRef.current;
      if (lock) setManualRoundNumber(lock.roundNumber);
    }
  }, [raceAuto]);

  // Hlavní rozhodčí = pole headJudgeUids NEBO starý headJudgeUid (B2, jako
  // pravidla). Nováček v tomto závodě: hlavní rozhodčí nebo správce
  const canManageNewbie = isStaffOrHeadJudge;
  // Opravy výsledků (tužka), zámek rundy se na ně nevztahuje (B4)
  const canCorrect = isStaffOrHeadJudge;
  // Rozhodčí závodu, hlavní rozhodčí nebo správce – smí uvolnit rundu
  // (live_progress, krok 4) stejně jako zapsat výsledek (pravidla
  // canWriteLiveAux, B5); běžný přihlášený návštěvník obrazovky ne
  const canWriteRaceAux = canWriteStationResult(race, viewer);

  // ── Samooprava seznamu rozhodčích závodu (návrh 3.1) ──────
  // Správce a hlavní rozhodčí po načtení stanovišť doplní do
  // races/{id}.liveJudgeUids rozhodčí stanovišť, kteří v něm chybí (závody
  // nastavené před fází 1). Jen přidává, jednou na načtení stanovišť (zamítnutý
  // zápis by se jinak opakoval s každým snímkem závodu); chyba je jen varování.
  // Stanoviště čte ZE SERVERU (jako Živé výsledky; návrh fáze 2, bod 4.6), ne
  // načtená obrazovkou: ta mohla přijít z mezipaměti a telefon hlavního
  // rozhodčího by vrátil právo rozhodčímu, kterého mezitím někdo výslovně
  // odebral. Bez spojení čtení selže a samooprava se přeskočí (jen varování,
  // nic se nezapíše).
  // Spouští se podle obsahu (rozhodčí stanovišť a seznam rozhodčích
  // závodu), ne podle identity polí – jiný snímek (metadata, livePublic)
  // server znovu nečte; odebrání rozhodčího jinde ho znovu ověří ze serveru.
  // Už ověřené stavy (i mezistav zamítnutého zápisu – vrácení by jinak
  // spustilo další zápis a zamítnutí ve smyčce)
  const selfHealedFor = useRef<Set<string>>(new Set());
  const selfHealKey = useMemo(
    () => JSON.stringify([allStations.map(s => [s.id, s.judgeUids || []]), race?.liveJudgeUids || []]),
    [allStations, race?.liveJudgeUids]
  );
  useEffect(() => {
    if (!raceId || !race || !setupReady || allStations.length === 0) return;
    if (!isStaffOrHeadJudge) return;
    if (selfHealedFor.current.has(selfHealKey)) return;
    selfHealedFor.current.add(selfHealKey);
    loadLiveStationsFromServer(raceId)
      .then(server => ensureLiveJudges(raceId, race, server))
      .catch(err => reportWarning("JudgeView.liveJudges", err, { raceId }));
  }, [raceId, race, setupReady, allStations, isStaffOrHeadJudge, selfHealKey]);

  // ── Nováček (E1, B7) ───────────────────────────────────────
  // Zápis jako nastavení závodu (utils/liveNewbies): rozhoduje řádek veřejné
  // startovní listiny (zapisuje se vždy); přihláška se srovná, když ji
  // zapisující smí číst (správce, hlavní rozhodčí) a střelec ji má. Zápis
  // nečeká na server – bez signálu by obrazovka visela (návrh 5.9); řádek
  // se hned promítne místně (živá startovní listina), zamítnutí ohlásí toast.
  const updateRegistrationOf = useCallback(
    (userId: string) => isStaffOrHeadJudge && (regNewbies === null || regNewbies.has(userId)),
    [isStaffOrHeadJudge, regNewbies]
  );
  const setShooterNewbie = (shooter: RoundShooter, value: boolean) => {
    if (!raceId || !shooter.userId || !canManageNewbie) return;
    setStartListNewbie(raceId, shooter.userId, value ? "ano" : "ne", {
      updateRegistration: updateRegistrationOf(shooter.userId),
    }).catch(err => {
      reportError("JudgeView.setStartListNewbie", err, { raceId, userId: shooter.userId });
      showToast(`Nováčka #${shooter.startNumber} se nepodařilo uložit: ${firebaseErrorMessage(err)}`, "error");
    });
  };

  // Trvalý zákaz nováčka mění profil střelce: superadmin každý, admin jen
  // profil střelce (canDisableNewbie podle role profilu – správce ji smí
  // přečíst). Profil + řádek listiny v jedné dávce; nečeká se na server.
  const disableNewbie = async (shooter: RoundShooter) => {
    if (!raceId || !shooter.userId || !isStaff) return;
    let targetRole: string | null | undefined;
    if (!viewer.isSuperAdmin) {
      try {
        const snap = await getDoc(doc(db, "users", shooter.userId));
        targetRole = snap.exists() ? ((snap.data() as { role?: string }).role ?? "") : "";
      } catch (err) {
        reportWarning("JudgeView.newbieTargetRole", err, { raceId, userId: shooter.userId });
        targetRole = undefined;
      }
    }
    if (!canDisableNewbie(viewer, targetRole)) {
      showToast("Trvalý zákaz nováčka u tohoto profilu může nastavit jen superadmin.", "error");
      return;
    }
    const ok = await confirm({
      title: "Trvale zakázat nováčka?",
      message: `Zakázat kategorii Nováček pro ${shooter.name} trvale? Tento střelec již nebude moci volit nováček status.`,
      confirmLabel: "Zakázat trvale",
      tone: "danger",
    });
    if (!ok) return;
    disableNewbiePermanently(raceId, shooter.userId, { updateRegistration: updateRegistrationOf(shooter.userId) })
      .catch(err => {
        reportError("JudgeView.disableNewbie", err, { raceId, userId: shooter.userId });
        showToast(`Trvalý zákaz nováčka #${shooter.startNumber} se nepodařilo uložit: ${firebaseErrorMessage(err)}`, "error");
      });
  };

  // ── Real-time: uvolnění rund (live_progress, krok 4) ──────
  const hasMyStations = myStations.length > 0;
  // Jen u běžícího závodu s automatickou rotací (ruční rundy je nepotřebují);
  // starý průběh r{N} se ignoruje. Vlastní uvolnění ve frontě (bez signálu)
  // je v místním pohledu hned.
  useEffect(() => {
    setMarkers(prev => (prev.length ? [] : prev));
    setMarkersLoaded(false);
    // live_progress čte jen přihlášený; návštěvník bez stanoviště ho nepotřebuje
    if (!raceId || !uid || !setupReady || !autoMode || !hasMyStations) return;
    const unsub = onSnapshot(
      collection(db, `races/${raceId}/live_progress`),
      (snap) => {
        setMarkers(markersOf(snap.docs.map(d => ({ id: d.id, data: d.data() as Record<string, unknown> }))));
        setMarkersLoaded(true);
      },
      (err) => reportWarning("JudgeView.markers", err, { raceId })
    );
    return unsub;
  }, [raceId, uid, setupReady, autoMode, hasMyStations]);

  // ── Koncepty vybraného stanoviště (D4) ─────────────────────
  // Rozepsané rány ze serveru: po obnovení aplikace uprostřed střelce se
  // vrátí, převzetí v dual módu ukáže skutečný počet ran. Koncept v paměti
  // (draftMap) má přednost – je novější.
  const [serverDrafts, setServerDrafts] = useState<{ [key: string]: number[] }>({});
  useEffect(() => {
    setServerDrafts(prev => (Object.keys(prev).length ? {} : prev));
    if (!raceId || !uid || !setupReady || !selectedStationId) return;
    const stationId = selectedStationId;
    const unsub = onSnapshot(
      query(collection(db, `races/${raceId}/live_drafts`), where("stationId", "==", stationId)),
      (snap) => {
        const map: { [key: string]: number[] } = {};
        snap.docs.forEach(d => {
          const data = d.data() as { shots?: unknown; startNumber?: unknown };
          if (typeof data.startNumber === "number" && Array.isArray(data.shots) && data.shots.length > 0) {
            map[`${stationId}_${data.startNumber}`] = data.shots as number[];
          }
        });
        setServerDrafts(map);
      },
      (err) => reportWarning("JudgeView.drafts", err, { raceId })
    );
    return unsub;
  }, [raceId, uid, setupReady, selectedStationId]);
  /** Rozepsané rány: v paměti, jinak koncept ze serveru. */
  const draftFor = (key: string): number[] | undefined =>
    draftMap[key] !== undefined ? draftMap[key] : serverDrafts[key];

  // ── Real-time: live_results ────────────────────────────────
  useEffect(() => {
    setResultsLoaded(false);
    if (!raceId || !setupReady) return;
    const unsub = onSnapshot(
      collection(db, `races/${raceId}/live_results`),
      (snap) => {
        const map: { [k: number]: LiveResult } = {};
        snap.docs.forEach(d => {
          const r = d.data() as LiveResult;
          map[r.startNumber] = r;
        });
        setLiveResults(map);
        setResultsLoaded(true);
      },
      (err) => reportWarning("JudgeView.results", err, { raceId })
    );
    return unsub;
  }, [raceId, setupReady]);

  // ── Aktuální runda u mého stanoviště ─────────────────────
  // Stanoviště, jak je teď v nastavení (lišta, výběr)
  const liveStation = selectedStationId
    ? allStations.find(s => s.id === selectedStationId) || null
    : null;
  if (liveStation) lastStationLabelRef.current = stationLabelOf(liveStation);
  // Rozpracovaný střelec na tomto stanovišti: platí pro něj nastavení
  // zmrazené při prvním zobrazení gongů (typ, dual, gongy, rány, čas)
  const lockHere = scoringLock && liveStation && scoringLock.stationId === liveStation.id ? scoringLock : null;
  const lockKey = lockHere ? `${lockHere.stationId}_${lockHere.startNumber}` : null;
  const frozenForLock = lockKey && frozenRef.current && frozenRef.current.key === lockKey ? frozenRef.current.station : null;
  const currentStation: LiveStation | null = frozenForLock || liveStation;

  // Runda na obrazovce: rozpracovaný střelec (i kdyby se rotace mezitím
  // posunula) → potvrzená / ručně zvolená → návrh automatické rotace
  const currentRound: LiveRound | null = React.useMemo(() => {
    if (!currentStation || roundShownNo === null) return null;
    return rounds.find(r => r.number === roundShownNo) || null;
  }, [currentStation, roundShownNo, rounds]);

  // Aktuální střelec podle indexu ručního toku (fallback uložení, ✏️ ve výběru)
  const currentShooter: RoundShooter | null = React.useMemo(() => {
    if (!currentRound) return null;
    const shooters = currentRound.shooters.filter(s => s.active);
    return shooters[manualShooterIndex] || null;
  }, [currentRound, manualShooterIndex]);

  // Automatická rotace: kdo je na tomto stanovišti v rundě čekaný a kdo
  // „mimo pořadí“ (chyběl na dřívějším stanovišti – uvolnění „chybí #N“).
  // Mimo rotaci (stanoviště nebo runda přidané starší verzí) čekaní všichni.
  const autoStop = rotationState && currentRound && currentStation
    ? stopOf(rotationState, currentRound.number, currentStation.id)
    : null;
  const withdrawnHere: ReadonlySet<number> = useMemo(() => new Set(autoStop ? autoStop.withdrawn : []), [autoStop]);

  // ── DUAL MODE computed ────────────────────────────────────
  const isDualStation = !!(currentStation?.dualShooter &&
    (currentStation.type === "gongs_sequential" || currentStation.type === "gongs_choice"));

  // ── DUAL: computed ───────────────────────────────────────
  /** Startovní čísla obsazená partnerem (jinými rozhodčími) */
  const partnerPickedSNs: Set<number> = React.useMemo(() => {
    const s = new Set<number>();
    Object.entries(dualActivePicks).forEach(([sn, u]) => {
      if (u !== uid) s.add(parseInt(sn));
    });
    return s;
  }, [dualActivePicks, uid]);

  /** True pokud je jen 1 střelec a partner ho má – čekám (automatická rotace: jen čekaní, ne „mimo pořadí“) */
  const isDualWaiting: boolean = React.useMemo(() => {
    if (!isDualStation || !currentRound || !currentStation || myDualPickedSN != null) return false;
    const stId = currentStation.id;
    const available = currentRound.shooters.filter(s =>
      s.active && !hasStationResult(liveResults, s.startNumber, stId) && !withdrawnHere.has(s.startNumber)
    );
    return available.length === 1 && partnerPickedSNs.has(available[0].startNumber);
  }, [isDualStation, currentRound, currentStation, myDualPickedSN, liveResults, partnerPickedSNs, withdrawnHere]);

  // ── Uložení výsledku stanoviště (gongy) ───────────────────
  // Jedna dávka (návrh 3.2): výsledek jen stanoviště stationId (mergeFields –
  // ostatní stanoviště, rozstřel a opravy hlavního rozhodčího zůstanou)
  // a smazání konceptu střel. enteredBy = uid rozhodčího (i u gongů).
  // Schránka „k odeslání“ (3.6): zápis se poznamená před odesláním (i s ranami
  // – jde odeslat znovu), potvrzení ho odstraní.
  //
  // Bez signálu (fáze 3, D1): zápis je ve frontě hned; na potvrzení serveru se
  // čeká nejvýš LIVE_SAVE_ACK_WAIT_MS, pak rozhodčí pokračuje dalším střelcem
  // („queued“) a výsledek se odešle sám. Zamítnutí v té lhůtě jde dál ke
  // gongům (hláška nad tlačítkem, rány zůstanou); pozdější zamítnutí ukáže
  // jen upozornění schránky (gongy už ukazují dalšího střelce). Bez trvalé
  // mezipaměti (fronta by zabitím aplikace zmizela) se čeká na server jako
  // dřív (návrh 5.4).
  const saveJudgeResult = useCallback(async (
    shooter: RoundShooter,
    roundNumber: number,
    stationId: string,
    result: JudgeShotsResult
  ): Promise<"saved" | "queued"> => {
    if (!raceId || !uid) return "saved";
    const startNum = shooter.startNumber;
    const errorKey = `${stationId}_${startNum}`;
    const entry: OutboxEntry = {
      raceId, startNumber: startNum, stationId, score: result.score, uid, at: Date.now(),
      shots: result.shots, gongValues: result.gongValues, roundNumber,
      userId: shooter.userId || "", sequenceNumber: shooter.sequenceNumber || "", name: shooter.name || "",
    };
    outboxAdd(entry);
    outboxSettled();
    setSaveErrors(prev => {
      if (!(errorKey in prev)) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
    const settled = saveStationResult(
      raceId,
      {
        startNumber: startNum,
        userId: shooter.userId,
        sequenceNumber: shooter.sequenceNumber,
        name: shooter.name,
        roundNumber,
      },
      stationId,
      { score: result.score, shots: result.shots, gongValues: result.gongValues, enteredBy: uid },
      liveResults[startNum]
    ).then(
      () => {
        outboxRemove(entry);
        dropDraftLocal(stationId, startNum);
        outboxSettled();
      },
      async (err) => {
        reportError("JudgeView.saveResult", err, { raceId, startNumber: startNum, stationId });
        // Hláška u výsledku (přežije přemountování gongů); smazané
        // stanoviště (pravidla) podle serveru srozumitelněji (F1)
        if (mountedRef.current) setSaveErrors(prev => ({ ...prev, [errorKey]: liveSaveErrorMessage(err) }));
        onSaveRejected();
        // Tato obrazovka mezitím mohla zmizet – zamítnutí se dozví i ta otevřená znovu
        notifyOutboxRejected();
        const code = err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
        if (code === "permission-denied") {
          void stationDeletedOnServer(raceId, stationId).then(deleted => {
            if (deleted && mountedRef.current) {
              setSaveErrors(prev => (prev[errorKey] === undefined ? prev
                : { ...prev, [errorKey]: liveSaveErrorMessage(err, { stationDeleted: true }) }));
            }
          });
        }
        throw err;
      }
    );
    // Zamítnutí po lhůtě už nikdo nečeká – obslouží ho upozornění schránky
    settled.catch(() => undefined);
    if (!firestoreQueueDurable()) {
      await settled;
      return "saved";
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        settled.then(() => "saved" as const),
        new Promise<"queued">(resolve => {
          timer = setTimeout(() => resolve("queued"), LIVE_SAVE_ACK_WAIT_MS);
        }),
      ]);
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }, [raceId, uid, liveResults, onSaveRejected, dropDraftLocal]);

  // Uložení nejde dokončit (obrazovka mezitím ztratila stanoviště, rundu
  // nebo přihlášení): chyba jde ke gongům – rány zůstanou a hláška se
  // ukáže; tiché „return“ by gongy vzaly jako uložené a rány zahodily
  const saveImpossible = (): Error =>
    new Error("Výsledek se neuložil – obrazovka nemá vybrané stanoviště nebo rundu. Vyberte je znovu a potvrďte.");

  /**
   * Automatická rotace: je runda na stanovišti po uložení střelce
   * `savedStartNumber` hotová podle dat (všichni čekaní mají výsledek, nebo
   * je uvolněná)? Mimo rotaci čekaní všichni aktivní.
   */
  const autoDoneAfter = useCallback((roundNumber: number, stationId: string, savedStartNumber: number): boolean => {
    if (!rotationState) return false;
    const stop = stopOf(rotationState, roundNumber, stationId);
    if (stop && (!stop.live || stop.released)) return true;
    const round = rounds.find(r => r.number === roundNumber);
    const expected = stop ? stop.expected : (round ? round.shooters.filter(s => s.active).map(s => s.startNumber) : []);
    return expected.every(sn => sn === savedStartNumber || hasStationResult(liveResults, sn, stationId));
  }, [rotationState, rounds, liveResults]);

  // ── Uložení výsledku + posun ──────────────────────────────
  const handleResultSaved = useCallback(async (
    result: JudgeShotsResult,
    shooterOverride?: RoundShooter   // FIX: picker může předat konkrétního střelce
  ) => {
    const effectiveShooter = shooterOverride || currentShooter;
    if (!raceId || !currentStation || !currentRound || !effectiveShooter || !uid) throw saveImpossible();

    const stationId = currentStation.id;
    const activeShooters = currentRound.shooters.filter(s => s.active);

    if (autoMode) {
      // Automatická rotace (krok 4): jen výsledek – poloha rund se odvodí
      // z výsledků (i ve frontě bez signálu), žádný další zápis. Runda
      // zůstane potvrzená, dokud není na stanovišti hotová podle dat (i s
      // právě uloženým střelcem) – ne podle indexu (střelci se vybírají
      // v libovolném pořadí); pak se předvybere další runda.
      await saveJudgeResult(effectiveShooter, currentRound.number, stationId, result);
      // Dobodovává se „mimo pořadí“ (runda zvolená ve výběru) a další takoví
      // tu ještě výsledek nemají – runda zůstane
      const sn = effectiveShooter.startNumber;
      const moreOffRoster = withdrawnHere.has(sn) && activeShooters.some(s =>
        s.startNumber !== sn && withdrawnHere.has(s.startNumber) && !hasStationResult(liveResults, s.startNumber, stationId)
      );
      if (autoDoneAfter(currentRound.number, stationId, sn) && !moreOffRoster) {
        setManualRoundNumber(null);
        setManualShooterIndex(0);
      } else {
        // Index jako ruční rundy (zámek rundy v řádku stejně)
        let nextIdx = manualShooterIndex + 1;
        while (nextIdx < activeShooters.length && hasStationResult(liveResults, activeShooters[nextIdx].startNumber, stationId)) nextIdx++;
        setManualShooterIndex(nextIdx < activeShooters.length ? nextIdx : 0);
      }
      setPickedShooterStartNum(null);
      setScoringLock(null);
      return;
    }

    // Ruční rundy – přejdi na dalšího střelce BEZ výsledku
    await saveJudgeResult(effectiveShooter, currentRound.number, stationId, result);
    // Najdi další index bez výsledku (přeskoč ty co jsou hotovi)
    let nextIdx = manualShooterIndex + 1;
    while (
      nextIdx < activeShooters.length &&
      hasStationResult(liveResults, activeShooters[nextIdx].startNumber, stationId)
    ) {
      nextIdx++;
    }
    if (nextIdx < activeShooters.length) {
      setManualShooterIndex(nextIdx);
    } else {
      // Všichni hotovi – reset na výběr rundy
      setManualRoundNumber(null);
      setManualShooterIndex(0);
    }
    setPickedShooterStartNum(null);
    setScoringLock(null);
  // currentShooter ponecháno v deps jako fallback (shooterOverride ho přepisuje v picker módu)
  }, [raceId, currentStation, currentRound, currentShooter, manualShooterIndex, uid, liveResults, autoMode, autoDoneAfter, withdrawnHere, saveJudgeResult]);

  // ── DUAL: Uložení výsledku ────────────────────────────────
  // Hlavička z aktuální rundy, jinak střelec, kterého gongy ukazují
  // (rozpracovaný střelec mezitím z rundy zmizel nebo se přesunul)
  const handleDualResultSaved = useCallback(async (result: JudgeShotsResult, shown: RoundShooter) => {
    if (!raceId || !currentStation || !currentRound || !uid) throw saveImpossible();
    const stationId = currentStation.id;
    const startNum  = shown.startNumber;
    const shooter = currentRound.shooters.find(s => s.startNumber === startNum) || shown;

    // Jen stanoviště stationId + smazání konceptu v jedné dávce
    await saveJudgeResult(shooter, currentRound.number, stationId, result);

    setDualLastEnteredStartNum(startNum);
    setPickedShooterStartNum(null);
    setScoringLock(null);
    // Uvolni svůj pick → zpět do picker listu (nečeká na server – bez
    // signálu by se gongy nikdy nedočkaly)
    releaseDualPick();
    // Rezervace v tomto dokumentu je uvolněná – změna klíče ji znovu neuvolňuje
    dualSnapshotRef.current = null;
    // Automatická rotace: runda na stanovišti hotová podle dat → další návrh
    if (autoMode && autoDoneAfter(currentRound.number, stationId, startNum)) {
      setManualRoundNumber(null);
      setManualShooterIndex(0);
    }
  }, [raceId, currentStation, currentRound, uid, saveJudgeResult, releaseDualPick, autoMode, autoDoneAfter]);

  // ── DUAL: Oprava posledního zadaného střelce ──────────────
  // Smaže jen stanoviště stationId (FieldPath) – ostatní stanoviště, rozstřel
  // a opravy hlavního rozhodčího zůstanou (návrh 3.2)
  const handleDualCorrectLast = useCallback(async () => {
    if (!raceId || !currentStation || !dualLastEnteredStartNum) return;
    const stationId = currentStation.id;
    const existing  = liveResults[dualLastEnteredStartNum];
    if (!existing?.stations?.[stationId]) return;

    try {
      await removeStationResultAsJudge(raceId, dualLastEnteredStartNum, stationId, existing);
      setDualLastEnteredStartNum(null);
    } catch (e) {
      reportError("JudgeView.dualCorrectLast", e, { raceId, startNumber: dualLastEnteredStartNum, stationId });
      showToast(`Výsledek #${dualLastEnteredStartNum} se nepodařilo smazat k opravě: ${firebaseErrorMessage(e)}`, "error");
    }
  }, [raceId, currentStation, dualLastEnteredStartNum, liveResults, showToast]);

  // ── DoneStartNumbers – střelci co již mají výsledek na tomto stanovišti ─
  const doneStartNumbers = React.useMemo((): Set<number> => {
    if (!currentStation || !currentRound) return new Set();
    const stId = currentStation.id;
    return new Set(
      currentRound.shooters
        .filter(s => s.active && hasStationResult(liveResults, s.startNumber, stId))
        .map(s => s.startNumber)
    );
  }, [currentStation, currentRound, liveResults]);
  const doneStartNumbersRef = useRef(doneStartNumbers);
  doneStartNumbersRef.current = doneStartNumbers;

  /**
   * Dual: převzetí střelce od partnera po potvrzení dialogem aplikace (I3).
   * Dialog čeká na rozhodčího – mezitím mohl partner výsledek uložit, pak se
   * nic nepřebírá.
   */
  const confirmTakeover = async (startNumber: number, name: string, shotsDone: number, shotsTotal: number) => {
    const ok = await confirm({
      title: "Převzít střelce?",
      message: shotsDone > 0
        ? `Převzít střelce #${startNumber} ${name}? Má ${shotsDone}/${shotsTotal} ran zadáno – pokračujete tam, kde partner skončil.`
        : `Převzít střelce #${startNumber} ${name} od druhého rozhodčího?`,
      confirmLabel: "Převzít",
    });
    if (!ok || !mountedRef.current) return;
    if (doneStartNumbersRef.current.has(startNumber)) {
      showToast(`Střelec #${startNumber} mezitím výsledek dostal – nic se nepřebírá.`, "info");
      return;
    }
    const taken = await takeoverDualShooter(startNumber, name);
    if (taken) {
      // Automatická rotace: převzetí střelce rundu na tomto telefonu potvrdí
      if (currentRound) adoptRound(currentRound.number);
      setPickedShooterStartNum(startNumber);
    }
  };

  // ─────────────────────────────────────────────────────────
  //  Gongy: koho obrazovka ukazuje
  // ─────────────────────────────────────────────────────────
  // Jeden výpočet pro vykreslení gongů, upozornění schránky (výsledek, jehož
  // hlášku gongy právě ukazují, upozornění neopakuje) i zmrazení nastavení
  const isGongsStation = currentStation?.type === "gongs_sequential" || currentStation?.type === "gongs_choice";
  const gongs = (() => {
    const active = currentRound?.shooters.filter(s => s.active) || [];
    // Dostupní střelci (bez výsledku na tomto stanovišti)
    const available = active.filter(s => !doneStartNumbers.has(s.startNumber));
    // Runda hotová
    const roundDone = available.length === 0 && active.length > 0;
    // Automatická rotace: „mimo pořadí“ (chyběl dřív, uvolnění) zvlášť –
    // jde bodovat, ale nikdy se nevybere sám; ruční rundy nikoho nevyřadí
    const pickable = available.filter(s => !withdrawnHere.has(s.startNumber));
    // Dual: dostupní jsou všichni bez výsledku a bez partnerova picku
    const effectiveAvailable = isDualStation
      ? pickable.filter(s => !partnerPickedSNs.has(s.startNumber))
      : pickable;
    const offRoster = available.filter(s => withdrawnHere.has(s.startNumber) && !(isDualStation && partnerPickedSNs.has(s.startNumber)));
    // Rozpracovaný střelec zůstává, i když mezitím dostal výsledek jinde nebo
    // „nenastoupil“ (hlavička zápisu z aktuální rundy, jinak z první rány)
    const lockedShooter = lockHere && currentRound
      ? currentRound.shooters.find(s => s.startNumber === lockHere.startNumber) || lockHere.shooter
      : null;
    // Pro dual: střelec je ten co mám v activePicks (myDualPickedSN); střelec
    // s výsledkem (uvolnění se nepovedlo) se znovu neukáže
    const pickedSN = isDualStation ? myDualPickedSN : pickedShooterStartNum;
    const pickedShooter = pickedSN != null
      ? active.find(s => s.startNumber === pickedSN && !(isDualStation && doneStartNumbers.has(s.startNumber))) || null
      : null;
    // Dual: NIKDY auto-pick – rozhodčí musí vždy explicitně kliknout
    // (auto-pick způsoboval problém: po Uvolnit se střelec okamžitě znovu přiřadil)
    // Automatická rotace: ani posledního nevybere, dokud runda není potvrzená
    // (gongy jiného střelce se bez ťuknutí neotevřou – kontrola, bod 4)
    const autoPickAllowed = !autoMode || manualRoundNumber !== null;
    // Dual: vlastní rezervace v dokumentu navržené rundy (po Zrušit, po
    // obnovení aplikace) gongy neotevře – až ťuknutí rundu potvrdí
    const shooterToPick = lockedShooter
      ? lockedShooter
      : isDualStation
        ? (myDualPickedSN != null && autoPickAllowed ? pickedShooter : null)
        : (pickedShooterStartNum == null && autoPickAllowed && effectiveAvailable.length === 1
            ? effectiveAvailable[0] // auto-pick jen pro non-dual
            : pickedShooter);
    // Runda je vybraná (ruční), potvrzená nebo navržená (automatická rotace)
    const roundShown = race !== null && (manualRoundNumber !== null || !!lockedShooter || (autoMode && roundShownNo !== null));
    // Gongy jsou na obrazovce (ne výběr střelce, „Runda hotová“ ani čekání)
    const visible = isGongsStation && roundShown && !!shooterToPick &&
      (!!lockedShooter || (!roundDone && !(isDualStation && isDualWaiting)));
    const shownKey = visible && currentStation && shooterToPick
      ? `${currentStation.id}_${shooterToPick.startNumber}`
      : null;
    return { active, available, roundDone, effectiveAvailable, offRoster, shooterToPick, roundShown, visible, shownKey };
  })();

  // Nastavení stanoviště pro zobrazeného střelce: zmrazí se při prvním
  // zobrazení gongů (časomíra, počet ran, gongy) a nové platí od dalšího
  // střelce. Bez gongů a bez rozpracovaného střelce se zahodí.
  if (gongs.shownKey && liveStation) {
    if (!frozenRef.current || frozenRef.current.key !== gongs.shownKey) {
      frozenRef.current = { key: gongs.shownKey, station: liveStation };
    }
  } else if (!lockHere) {
    frozenRef.current = null;
  }
  const gongsStation: LiveStation | null =
    gongs.shownKey && frozenRef.current && frozenRef.current.key === gongs.shownKey ? frozenRef.current.station : currentStation;
  const stationConfigChanged = !!gongs.shownKey && !!liveStation && stationConfigKey(gongsStation) !== stationConfigKey(liveStation);

  // Rozpracovaný střelec: první rána ho zamkne, návrat na 0 ran odemkne
  // (koncept v paměti; přijatý výsledek koncept smaže)
  const shownDraftLength = gongs.shownKey ? (draftFor(gongs.shownKey)?.length ?? 0) : 0;
  const shownShooterRef = useRef<RoundShooter | null>(null);
  shownShooterRef.current = gongs.shooterToPick;
  const shownRoundNumber = currentRound ? currentRound.number : null;
  useEffect(() => {
    const shooter = shownShooterRef.current;
    if (!gongs.shownKey || !shooter || !selectedStationId || shownRoundNumber === null) return;
    const lock = scoringLockRef.current;
    if (shownDraftLength > 0) {
      if (!lock) {
        setScoringLock({ stationId: selectedStationId, roundNumber: shownRoundNumber, startNumber: shooter.startNumber, shooter });
      }
    } else if (lock && `${lock.stationId}_${lock.startNumber}` === gongs.shownKey) {
      setScoringLock(null);
    }
  }, [gongs.shownKey, shownDraftLength, selectedStationId, shownRoundNumber]);

  // Dual: střelce mi převzal druhý rozhodčí – rozpracovaný se uvolní
  useEffect(() => {
    if (!lockHere || !isDualStation || !uid) return;
    const owner = dualActivePicks[String(lockHere.startNumber)];
    if (owner && owner !== uid) setScoringLock(null);
  }, [lockHere, isDualStation, dualActivePicks, uid]);

  // ── Automatická rotace: konec potvrzené rundy podle dat (rozbor §5.2) ──
  // Vlastní uložení rundu ukončí hned (handleResultSaved). Jinak se potvrzená
  // runda zahodí (→ další návrh), když na stanovišti PŘEJDE do stavu
  // „hotová“ (výsledek z druhého telefonu dvojice, uvolnění) nebo když její
  // výsledky na stanovišti zmizí (Smazat výsledky, smazání hlavním
  // rozhodčím) – nikdy pod rozpracovaným ani vybraným střelcem (počká se na
  // Potvrdit / Zrušit). Runda zvolená ve výběru už hotová (dobodovat
  // střelce mimo pořadí) zůstane.
  const adoptedKey = autoMode && manualRoundNumber !== null && selectedStationId ? `${selectedStationId}_${manualRoundNumber}` : null;
  const adoptedRound = adoptedKey ? rounds.find(r => r.number === manualRoundNumber) || null : null;
  const adoptedStop = adoptedKey && rotationState && selectedStationId && manualRoundNumber !== null
    ? stopOf(rotationState, manualRoundNumber, selectedStationId)
    : null;
  const adoptedDone = !!adoptedRound && !!selectedStationId && (adoptedStop
    ? adoptedStop.done
    : adoptedRound.shooters.filter(s => s.active).every(s => hasStationResult(liveResults, s.startNumber, selectedStationId)));
  const adoptedCount = adoptedRound && selectedStationId
    ? adoptedRound.shooters.filter(s => hasStationResult(liveResults, s.startNumber, selectedStationId)).length
    : 0;
  const adoptionBlocked = !!lockHere || pickedShooterStartNum !== null || myDualPickedSN !== null;
  const adoptedRef = useRef<{ key: string; done: boolean; count: number } | null>(null);
  useEffect(() => {
    if (!adoptedKey) {
      adoptedRef.current = null;
      return;
    }
    const prev = adoptedRef.current;
    if (!prev || prev.key !== adoptedKey) {
      adoptedRef.current = { key: adoptedKey, done: adoptedDone, count: adoptedCount };
      return;
    }
    // Pod střelcem se výchozí stav nemění – přechod se vyhodnotí po něm
    if (adoptionBlocked) return;
    adoptedRef.current = { key: adoptedKey, done: adoptedDone, count: adoptedCount };
    if ((!prev.done && adoptedDone) || (prev.count > 0 && adoptedCount === 0)) {
      setManualRoundNumber(null);
      setManualShooterIndex(0);
    }
  }, [adoptedKey, adoptedDone, adoptedCount, adoptionBlocked]);

  /**
   * Potvrzení rundy na tomto telefonu (ťuknutí na střelce, rezervace,
   * převzetí). Index jako výběr rundy u ručních rund (první bez výsledku) –
   * zámek rundy v řádku nad ZÁSAH/NULA se chová stejně.
   */
  const adoptRound = (roundNumber: number) => {
    if (!autoMode || manualRoundNumber !== null || !currentStation) return;
    setManualRoundNumber(roundNumber);
    const active = rounds.find(r => r.number === roundNumber)?.shooters.filter(s => s.active) || [];
    const idx = active.findIndex(s => !hasStationResult(liveResults, s.startNumber, currentStation.id));
    setManualShooterIndex(idx >= 0 ? idx : 0);
  };

  /**
   * Uvolnění rundy na stanovišti (krok 4, R3): „Runda hotová – chybí #N“ /
   * terč „odstřílela“. Zápis bez čekání (bez signálu ve frontě – runda jde
   * dál i na tomto telefonu), zamítnutí hláškou. Pravidla: rozhodčí závodu,
   * hlavní rozhodčí, správce (canWriteLiveAux) – beze změny pravidel.
   */
  const writeRelease = useCallback((stationId: string, roundNumber: number, released: boolean, missing: number[] = []) => {
    if (!raceId || !uid) return;
    const data: DocumentData = released
      ? { stationId, roundNumber, released: true, missing, by: uid, at: serverTimestamp() }
      : { stationId, roundNumber, released: false, by: uid, at: serverTimestamp() };
    setDoc(doc(db, `races/${raceId}/live_progress`, markerDocId(stationId, roundNumber)), data, { merge: true }).catch(err => {
      reportError("JudgeView.release", err, { raceId, stationId, roundNumber, released });
      showToast(
        released
          ? `Uvolnění rundy ${roundNumber} se neuložilo: ${firebaseErrorMessage(err)}`
          : `Vrácení uvolnění rundy ${roundNumber} se neuložilo: ${firebaseErrorMessage(err)}`,
        "error"
      );
    });
  }, [raceId, uid, showToast]);

  /** „Runda hotová – chybí #N“ s potvrzením (střelec chybí nebo odstoupil). */
  const confirmReleaseMissing = async (roundNumber: number, stationId: string, missing: RoundShooter[]) => {
    const st = allStations.find(s => s.id === stationId);
    const list = missing.map(s => `#${s.startNumber}${s.name ? ` ${s.name}` : ""}`).join(", ");
    const ok = await confirm({
      title: `Runda ${roundNumber} hotová?`,
      message: `Na stanovišti ${st ? stationLabelOf(st) : stationId} chybí ${list}. Runda půjde dál a další stanoviště ${missing.length > 1 ? "je" : "ho"} nebudou čekat – ukáže se stranou (Mimo pořadí) a jde dál bodovat, kdyby se vrátil. Hlavní rozhodčí může uvolnění vrátit.`,
      confirmLabel: "Runda hotová",
    });
    if (!ok || !mountedRef.current) return;
    writeRelease(stationId, roundNumber, true, missing.map(s => s.startNumber));
  };

  /** Terč: „Runda N odstřílela – poslat dál“ s potvrzením (bez seznamu chybějících). */
  const confirmReleaseTarget = async (roundNumber: number, stationId: string) => {
    const ok = await confirm({
      title: `Runda ${roundNumber} odstřílela?`,
      message: "Runda půjde na další stanoviště. Výsledky terčů jde zadat i potom.",
      confirmLabel: "Poslat dál",
    });
    if (!ok || !mountedRef.current) return;
    writeRelease(stationId, roundNumber, true, []);
  };

  // ── Progress výsledků (I8) ────────────────────────────────
  // Skutečné dokončení: uložené výsledky stanovišť aktivních střelců ÷
  // aktivní střelci × stanoviště závodu. Dřív počet dokumentů výsledků ÷
  // střelci – střelec s jedním stanovištem ze tří se počítal jako hotový.
  // Sdílené s tabulí (fáze 4a, A4 – utils/liveProgress.ts).
  const { done: progressDone, expected: progressExpected, pct: progressPct } = useMemo(() => {
    const active: number[] = [];
    rounds.forEach(r => r.shooters.forEach(sh => { if (sh.active) active.push(sh.startNumber); }));
    return liveProgress(active, allStations.map(st => st.id), liveResults);
  }, [rounds, liveResults, allStations]);

  // ── Ukončení závodu ───────────────────────────────────────
  const [finishing, setFinishing] = useState(false);
  // Ukončení: správce nebo hlavní rozhodčí, jen běžící závod (B4); zveřejnění
  // výsledků na obrazovce „Závod ukončen“ a veřejné výsledky v liště taky
  const canFinishRace = canFinishRaceOf(race, viewer);
  const canPublish = canPublishResults(race, viewer);
  const canToggleLivePublic = isStaffOrHeadJudge;

  // F2: závod mohl mezitím ukončit jiný hlavní rozhodčí nebo správce (druhé
  // zařízení, zastaralá obrazovka). Před publikací se stav ověří na serveru
  // a stav se zapíše transakcí jen z „běží“ – finishedAt (od něj běží 48 h
  // na rozstřel) se tak nikdy neposune. Pravidla (otázka 15, 81bf5b3) změnu
  // finishedAt ukončeného závodu zamítnou všem včetně správce; transakce
  // chrání i proti starší verzi pravidel a dává srozumitelnou hlášku.
  //
  // Dvojí potvrzení dialogy aplikace (I3, rozhodnutí 9); výsledek hláškou,
  // upozornění publikace zůstane na obrazovce „Závod ukončen“
  const [finishNotice, setFinishNotice] = useState<string | null>(null);
  const finishingRef = useRef(false);
  // Přehled chybějících výsledků před ukončením (fáze 4a, F2, otázka 9):
  // vlastní čtení ze SERVERU jen pro přehled – publikace si po potvrzení čte
  // znovu (hlavní rozhodčí může nad dialogem čekat minuty). Ukončit jde i tak.
  const FINISH_SUMMARY_TIMEOUT_MS = 10000;
  const finishSummary = async (): Promise<string> => {
    if (!raceId) return "";
    try {
      const [results, stations, serverRounds] = await Promise.all([
        loadLiveResultsFromServer(raceId),
        loadLiveStationsFromServer(raceId),
        loadLiveRoundsFromServer(raceId),
      ]);
      const active: number[] = [];
      serverRounds.forEach(r => (r.shooters || []).forEach(sh => { if (sh.active) active.push(sh.startNumber); }));
      const missing = missingResultsText(missingResults(active, stations, results)) || "Výsledky jsou zadané ze všech stanovišť.";
      // Výsledky mimo aktivní střelce se nezveřejní (otázka 17) – hlavní
      // rozhodčí to může ještě před ukončením vyřešit
      const activeSet = new Set(active);
      const stationIds = stations.map(st => st.id);
      const outside = Object.keys(results)
        .map(Number)
        .filter(sn => Number.isFinite(sn) && !activeSet.has(sn) && hasLiveResult(results[sn], stationIds))
        .sort((a, b) => a - b);
      return outside.length > 0
        ? `${missing} Výsledky mimo aktivní střelce se nezveřejní: ${outside.slice(0, 12).map(sn => `#${sn}`).join(", ")}${outside.length > 12 ? ", …" : ""}.`
        : missing;
    } catch (err) {
      reportWarning("JudgeView.finishSummary", err, { raceId });
      return "Chybějící výsledky nelze ověřit bez připojení k serveru.";
    }
  };

  const handleFinishRace = async () => {
    if (!raceId || !race || finishingRef.current) return;
    finishingRef.current = true;
    try {
      // Čtení přehledu může na slabém signálu trvat – tlačítko ukazuje práci,
      // po 10 s se dialog ukáže bez přehledu
      setFinishing(true);
      const summary = await Promise.race([
        finishSummary(),
        new Promise<string>(resolve => setTimeout(() => resolve("Chybějící výsledky se nepodařilo včas ověřit."), FINISH_SUMMARY_TIMEOUT_MS)),
      ]);
      if (!mountedRef.current) return;
      setFinishing(false);
      const first = await confirm({
        title: "Ukončit závod?",
        message: (
          <>
            <p className="mb-2">{`Opravdu chcete ukončit závod „${race.name}“? Rozhodčí ztratí přístup k zadávání výsledků.`}</p>
            <p className="mb-0 small">{summary}</p>
          </>
        ),
        confirmLabel: "Ukončit závod",
        tone: "danger",
      });
      if (!first) return;
      const second = await confirm({
        title: "Poslední upozornění",
        message: "Ukončení závodu je nevratné. Pokračovat?",
        confirmLabel: "Ano, ukončit",
        tone: "danger",
      });
      if (!second || !mountedRef.current) return;
      await finishRace();
    } finally {
      finishingRef.current = false;
      if (mountedRef.current) setFinishing(false);
    }
  };

  const finishRace = async () => {
    if (!raceId || !race) return;
    setFinishing(true);
    setFinishNotice(null);
    try {
      // 0. Stav závodu, výsledky i stanoviště ze SERVERU (návrh 3.5) – místní
      //    mezipaměť může po zápisech jednoho stanoviště offline držet
      //    neúplný dokument a stanoviště mohou být zastaralá. Offline skončí
      //    srozumitelnou chybou a závod se neukončí.
      let loaded: [DocumentData | undefined, { [startNum: number]: LiveResult }, LiveStation[]];
      try {
        loaded = await Promise.all([
          getDocFromServer(doc(db, "races", raceId)).then(snap => (snap.exists() ? snap.data() : undefined)),
          loadLiveResultsFromServer(raceId),
          loadLiveStationsFromServer(raceId),
        ]);
      } catch (loadErr) {
        reportWarning("JudgeView.finishLoad", loadErr, { raceId });
        throw new Error(
          "Výsledky se nepodařilo načíst ze serveru – závod lze ukončit jen s připojením k internetu."
        );
      }
      const [serverRace, serverResults, serverStations] = loaded;
      if (!serverRace || serverRace.status !== "active") {
        // Už ukončený (nebo vrácený do přípravy) – nic se nepublikuje ani nezapíše
        if (serverRace && serverRace.status === "finished") showToast(RACE_ALREADY_FINISHED_MESSAGE, "info");
        else showToast(RACE_NOT_RUNNING_MESSAGE, "error"); // zůstane do zavření (dřív okno)
        return;
      }
      // Bez stanovišť by publikace dala všem 0 bodů
      if (serverStations.length === 0) {
        throw new Error("Závod nemá na serveru žádná stanoviště – nelze ho ukončit s publikací.");
      }

      // 1. Publikuj výsledky → results_entries + results_stages (batch, idempotentní).
      //    Dělá se PŘED změnou statusu: když publikace selže, závod zůstane
      //    „active“ a ukončení lze zopakovat – žádný stav „ukončeno, ale
      //    publikováno jen 5 střelců“. Rundy se nepředávají: publikace je
      //    přečte ze serveru (návrh fáze 2, bod 4.5). Offline publikace
      //    skončí chybou.
      const published = await publishLiveResults({
        raceId,
        raceDate: race.date,
        liveResults: serverResults,
        stations: serverStations,
        // Hlavní rozhodčí bez role čte jen veřejnou startovní listinu (střelci
        // bez údajů si ponechají kategorii z dřívější publikace); správce ji
        // před publikací synchronizuje z profilů a přihlášek
        asStaff: isStaff,
      });
      const notice = publishNoticeText(published, isStaff);

      // 2. Teprve teď status finished (čas serveru) – transakcí jen z „běží“:
      //    souběžné ukončení z jiného zařízení finishedAt neposune
      // Stav, který transakce viděla ("" = závod neexistuje)
      let statusSeen: unknown = "active";
      let finishedNow = false;
      try {
        finishedNow = await runTransaction(db, async (tx) => {
          const snap = await tx.get(doc(db, "races", raceId));
          statusSeen = snap.exists() ? (snap.data() as { status?: unknown }).status : "";
          if (statusSeen !== "active") return false;
          tx.update(doc(db, "races", raceId), { status: "finished", finishedAt: serverTimestamp() });
          return true;
        });
      } catch (statusErr) {
        const code = statusErr && typeof statusErr === "object" ? (statusErr as { code?: unknown }).code : undefined;
        if (code !== "permission-denied") throw statusErr;
        // Pravidla zamítnou ukončení už ukončeného závodu (hlavnímu
        // rozhodčímu i správci – finishedAt je po ukončení neměnný) – jen když na serveru opravdu ukončený je; jinak (odebraný
        // hlavní rozhodčí…) skutečná chyba
        reportWarning("JudgeView.finishStatus", statusErr, { raceId });
        let finishedOnServer = false;
        try {
          const again = await getDocFromServer(doc(db, "races", raceId));
          finishedOnServer = again.exists() && (again.data() as { status?: unknown }).status === "finished";
        } catch {
          finishedOnServer = false;
        }
        if (!finishedOnServer) throw statusErr;
        statusSeen = "finished";
      }
      if (!finishedNow) {
        // Ukončil ho mezitím někdo jiný, nebo ho vrátili do přípravy (výsledky
        // se ale už zveřejnily – publikace proběhla; ověřit je – hláška zůstane)
        if (statusSeen === "finished") showToast(RACE_ALREADY_FINISHED_MESSAGE, "info");
        else showToast(RACE_NOT_RUNNING_AFTER_PUBLISH_MESSAGE, "error");
        return;
      }

      showToast(`Závod ukončen. Publikováno ${published.written} střelců.`, "success");
      // Upozornění publikace (střelci bez kategorie…) zůstane na obrazovce
      // „Závod ukončen“ – hláška by zmizela dřív, než si ji přečte
      if (notice) setFinishNotice(notice);

      // 3. Dozveřejnění (fáze 4a, F2b): výsledek, který server přijal mezi
      //    čtením pro publikaci a přepnutím stavu, by se jinak nezveřejnil
      //    nikdy. Po ukončení ho pravidla rozhodčím stanovišť už nepustí –
      //    stačí jedno nové čtení a dílčí publikace rozdílů.
      try {
        const after = await loadLiveResultsFromServer(raceId);
        const late = changedLiveResults(serverResults, after, serverStations.map(st => st.id));
        if (late.length > 0) {
          await publishLiveResults({
            raceId,
            raceDate: race.date,
            liveResults: after,
            stations: serverStations,
            onlyStartNumbers: late,
            asStaff: isStaff,
          });
        }
      } catch (lateErr) {
        reportWarning("JudgeView.finishLate", lateErr, { raceId });
        showToast(
          "Závod je ukončen, ale výsledky odeslané během ukončování se nepodařilo zveřejnit – přepublikujte je v Živých výsledcích.",
          "warning"
        );
      }
    } catch (e: any) {
      reportError("JudgeView.finishRace", e, { raceId });
      showToast(`Chyba při ukončení závodu: ${e.message} Závod NEBYL ukončen – zkuste to znovu.`, "error");
    } finally {
      if (mountedRef.current) setFinishing(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  //  Render helpers
  // ─────────────────────────────────────────────────────────

  const [isOnline, setIsOnline] = React.useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  // Úzká obrazovka – s posluchačem otočení a změny velikosti (I4)
  const isMobile = useIsMobile();
  // Nová verze aplikace na serveru (I9) – upozornění, nikdy obnovení samo
  const newAppVersion = useNewAppVersion(setupReady);
  React.useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online",  on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Dual bez spojení: varování zmizí, až je spojení se serverem zpátky
  useEffect(() => {
    if (isOnline && live.serverSynced) setDualOffline(false);
  }, [isOnline, live.serverSynced]);

  // Slabé spojení (D2): připojení hlásí prohlížeč, ale posluchače déle než
  // WEAK_SIGNAL_MS nemají stav ze serveru (navigator.onLine na slabém
  // signálu lže)
  const serverSynced = live.serverSynced;
  const [weakSignal, setWeakSignal] = useState(false);
  useEffect(() => {
    if (!isOnline || serverSynced || !setupReady) {
      setWeakSignal(false);
      return;
    }
    const timer = setTimeout(() => setWeakSignal(true), WEAK_SIGNAL_MS);
    return () => clearTimeout(timer);
  }, [isOnline, serverSynced, setupReady]);

  // Neodeslané výsledky (D1): ve schránce a ještě ne v upozornění na
  // neuložené (ty čekají na „Rozumím“)
  const unsentCount = (() => {
    if (!raceId || !uid) return 0;
    const lost = new Set(lostEntries.map(outboxKeyOf));
    return outboxList(raceId, uid).filter(e => !lost.has(outboxKeyOf(e))).length;
  })();

  // „Odeslat znovu“ (bod 5.4): výsledek, který na serveru není a má uložené
  // rány (zápis se ztratil s frontou jen v paměti) – jen za běhu a na
  // existující stanoviště; hlavička z aktuální rundy, jinak ze záznamu
  // (a výsledek na obrazovce pořád chybí – novější zadání ani opravu
  // nepřepíše); před odesláním se server ověří ještě jednou
  const canResendLost = (e: LostEntry): boolean =>
    setupReady && e.serverScore === null && Array.isArray(e.shots) &&
    allStations.some(st => st.id === e.stationId) &&
    liveResults[e.startNumber]?.stations?.[e.stationId] === undefined;
  const resendLost = async (e: LostEntry) => {
    if (!canResendLost(e) || !raceId) return;
    let serverScore: number | null = null;
    try {
      const snap = await getDocFromServer(liveResultRef(raceId, e.startNumber));
      const st = snap.exists() ? (snap.data() as LiveResult).stations?.[e.stationId] : undefined;
      serverScore = st && typeof st.score === "number" ? st.score : null;
    } catch (err) {
      reportWarning("JudgeView.resendCheck", err, { raceId });
      showToast("Bez spojení se serverem nejde ověřit, že výsledek mezitím nikdo nezadal – zkuste to znovu se signálem.", "error");
      return;
    }
    if (serverScore !== null) {
      // Mezitím uložený (jinde nebo opožděně) – jen aktualizovat upozornění
      const found = serverScore;
      setLostEntries(prev => prev.map(x => (outboxKeyOf(x) === outboxKeyOf(e) ? { ...x, serverScore: found } : x)));
      return;
    }
    const fromRound = rounds.flatMap(r => r.shooters.map(sh => ({ sh, round: r.number })))
      .find(x => x.sh.startNumber === e.startNumber);
    const shooter: RoundShooter = fromRound ? fromRound.sh : {
      startNumber: e.startNumber, userId: e.userId || "", sequenceNumber: e.sequenceNumber || "",
      name: e.name || "", active: true,
    };
    const roundNumber = fromRound ? fromRound.round : (e.roundNumber ?? 0);
    // Upozornění pro starý záznam zmizí – nové zadání ho ve schránce nahradí
    setLostEntries(prev => prev.filter(x => outboxKeyOf(x) !== outboxKeyOf(e)));
    saveJudgeResult(shooter, roundNumber, e.stationId, { score: e.score, shots: e.shots || [], gongValues: e.gongValues })
      .catch(() => undefined); // zamítnutí ohlásí schránka znovu
  };

  // Schránka neodeslaných výsledků (návrh 3.6): po otevření obrazovky online
  // (a po každém obnovení připojení) se zbylé záznamy porovnají se serverem –
  // i zápisy zamítnuté po obnovení stránky, které SDK nijak neohlásí
  useEffect(() => {
    if (!raceId || !uid || !isOnline) return;
    void reconcileOutbox();
  }, [raceId, uid, isOnline, reconcileOutbox]);

  // Závod přestal běžet (ukončen, vrácen před spuštění): hlášky u gongů
  // i řádků terčů zmizely s obrazovkou – schránku porovnat, upozornění zůstane
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = race?.status;
    if (prev === "active" && race?.status !== "active") void reconcileOutbox();
  }, [race?.status, reconcileOutbox]);

  // Toasty pod horní lištou rozhodčího, ne přes ni (návrh 3.6)
  const topBarRef = useToastTopBelow();

  /** Položka výběru stanoviště (✏️ = rozepsaný koncept aktuálního střelce). */
  const stationOption = (st: LiveStation) => {
    const hasDraft = currentShooter &&
      (draftFor(`${st.id}_${currentShooter.startNumber}`)?.length ?? 0) > 0;
    return (
      <option key={st.id} value={st.id}>
        {st.number}. {st.name}{hasDraft ? " ✏️" : ""}
      </option>
    );
  };

  /**
   * „N zbývá · a/b zadáno“ a „Zrušit“ u vybrané rundy (I4 – dřív dvě kopie):
   * pod výběrem rundy (bez Zrušit, je-li runda zamčená), nebo vedle výběrů
   * na jednom řádku (obě hodnoty vybrané). Mezery okraji, ne flex gap
   * (Chrome 80 – I6).
   */
  const roundSummary = (inline: boolean, roundLocked: boolean) => {
    const r = rounds.find(r => r.number === manualRoundNumber);
    const active = r?.shooters.filter(s => s.active) || [];
    const stId = currentStation ? currentStation.id : "";
    const done = active.filter(s => liveResults[s.startNumber]?.stations?.[stId] !== undefined).length;
    return (
      <div
        className={`d-flex align-items-center${inline ? "" : " mt-1"}`}
        style={inline ? { flex: "0 0 auto", alignSelf: "center" } : { flexWrap: "nowrap", minWidth: 0 }}
      >
        <span className="text-muted text-nowrap me-2" style={{ fontSize: "0.75rem" }}>
          {active.length - done} zbývá · {done}/{active.length} zadáno
        </span>
        {(inline || !roundLocked) && (
          <button
            className={`btn btn-sm btn-outline-secondary py-0 text-nowrap${inline ? "" : " ms-auto"}`}
            style={{ fontSize: "0.75rem", flexShrink: 0 }}
            onClick={cancelRound}
          >
            <i className="bi bi-x me-1"/>Zrušit
          </button>
        )}
      </div>
    );
  };

  /** „Zrušit“ u rundy: zpět na výběr rundy (i rozpracovaný střelec – koncept zůstane). */
  const cancelRound = () => {
    // Automatická rotace + dual: vlastní rezervace se uvolní (druhý
    // rozhodčí střelce uvidí volného; ruční rundy beze změny)
    if (autoMode && isDualStation && myDualPickedSN !== null) {
      releaseDualPick();
      dualSnapshotRef.current = null;
    }
    setManualRoundNumber(null);
    setManualShooterIndex(0);
    setPickedShooterStartNum(null);
    setScoringLock(null);
  };

  // Upozornění na neuložené výsledky – v každém stavu obrazovky, který
  // rozhodčí vidí (běžící i ukončený závod, hlášky); nepřekrývá horní lištu
  // Smazané stanoviště (F3): id se od fáze 2 nerovná číslu – „st7“ by se
  // spletlo s živým stanovištěm číslo 7. Smazané jen podle známého seznamu
  // (běžící závod načtený, nebo popisky upozornění ze čtení stanovišť).
  const stationLabel = (stationId: string): string => {
    const st = allStations.find(s => s.id === stationId);
    if (st) return stationLabelOf(st);
    if (noticeStations[stationId]) return noticeStations[stationId];
    const listKnown = setupReady || Object.keys(noticeStations).length > 0;
    return listKnown ? `smazané stanoviště (${stationId})` : stationId;
  };
  /**
   * Výsledky do upozornění: jen zápisy, které ve schránce pořád jsou (potvrzený
   * nebo znovu zadaný výsledek zmizí); za běhu bez výsledku, jehož hlášku
   * právě ukazují gongy (shownGongKey = "{stationId}_{startNumber}") – hláška
   * u gongů a upozornění nikdy oba najednou.
   */
  const noticeEntries = (shownGongKey: string | null): LostEntry[] => {
    if (lostEntries.length === 0 || !raceId || !uid) return [];
    const pending = new Set(outboxList(raceId, uid).map(outboxKeyOf));
    const hidden = race?.status === "active" && shownGongKey !== null && saveErrors[shownGongKey] !== undefined
      ? shownGongKey
      : null;
    return lostEntries.filter(e =>
      pending.has(outboxKeyOf(e)) && `${e.stationId}_${e.startNumber}` !== hidden);
  };
  const outboxNotice = (opts: { band?: boolean; style?: React.CSSProperties; shownGongKey?: string | null } = {}) => {
    const entries = noticeEntries(opts.shownGongKey ?? null);
    return entries.length > 0 ? (
      <LiveOutboxNotice
        entries={entries}
        stationLabel={stationLabel}
        onAcknowledge={() => acknowledgeLost(entries)}
        onResend={resendLost}
        canResend={canResendLost}
        band={opts.band}
        style={opts.style}
      />
    ) : null;
  };

  // Bez přihlášení se závod nenačítá (loading zůstane) – rovnou výzva k přihlášení
  if (authLoading || (uid && loading)) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      <div className="text-center text-muted">
        <div className="spinner-border mb-2" />
        <div>Načítám závod...</div>
        {!isOnline && (
          <div className="small mt-2">
            <i className="bi bi-wifi-off me-1" />Jste offline – závod se načte po připojení k internetu.
          </div>
        )}
      </div>
    </div>
  );

  if (!uid) return (
    <div className="container mt-5 text-center">
      <div className="alert alert-warning">
        Pro přístup k rozhodčímu musíte být přihlášeni.
      </div>
      <button className="btn btn-success" onClick={() => navigate("/login")}>
        Přihlásit se
      </button>
    </div>
  );

  if (error) return (
    <div className="container mt-5">
      {outboxNotice({ style: { marginBottom: "1rem" } })}
      <div className="alert alert-danger">{error}</div>
      <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>← Zpět</button>
    </div>
  );

  // Závod ukončen → rozhodčí vidí jen info obrazovku. Před kontrolou
  // stanovišť: u ukončeného závodu se stanoviště nenačítají (po obnovení
  // stránky tu hlavní rozhodčí skrývá a zveřejňuje výsledky)
  if (race?.status === "finished") return (
    <div style={{ minHeight: "100vh", background: "#0a1628", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
      {outboxNotice({ style: { width: "100%", maxWidth: 480, marginBottom: "1.5rem" } })}
      <div style={{ fontSize: "4rem" }}>🏁</div>
      <h3 className="mt-3">Závod ukončen</h3>
      <div className="text-muted mb-4">{race.name}</div>
      {/* Upozornění publikace z ukončení na tomto zařízení (dřív okno prohlížeče) */}
      {finishNotice && (
        <div className="alert alert-warning small mb-4" role="status" style={{ width: "100%", maxWidth: 480 }}>
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {finishNotice}
        </div>
      )}
      <div className="d-flex flex-column gap-2 align-items-center">
        <a className="btn btn-outline-light" href={`/display/${raceId}`} target="_blank" rel="noreferrer">
          <i className="bi bi-bar-chart me-2" />Zobrazit výsledky
        </a>
        {/* Hlavní rozhodčí / správce může ručně skrýt/zobrazit výsledky */}
        {canPublish && (
          <button
            className={`btn btn-sm mt-2 ${race.livePublic === false ? "btn-outline-success" : "btn-outline-warning"}`}
            onClick={async () => {
              const next = race.livePublic === false;
              try {
                if (!raceId) return;
                await updateDoc(doc(db, "races", raceId), { livePublic: next });
              } catch (e) {
                reportWarning("JudgeView.livePublicFinished", e, { raceId });
                showToast(`Zveřejnění výsledků se nepodařilo změnit: ${firebaseErrorMessage(e)}`, "error");
              }
            }}
          >
            <i className={`bi bi-eye${race.livePublic === false ? "" : "-slash"} me-2`} />
            {race.livePublic === false
              ? "Zveřejnit výsledky (zobrazí banner v appce)"
              : "Skrýt výsledky (odstraní banner z appky)"}
          </button>
        )}
        <div className="text-muted mt-3" style={{ fontSize: "0.78rem", textAlign: "center" }}>
          Výsledky se v appce zobrazují 2 dny po ukončení závodu,<br />
          pokud nejsou ručně skryty.
        </div>
      </div>
    </div>
  );

  // Závod ještě nebyl spuštěn (nebo ho správce vrátil před spuštění) – po
  // spuštění se obrazovka rozhodčího načte sama (posluchač závodu)
  if (race && race.status !== "active") return (
    <div className="container mt-5 text-center">
      {outboxNotice({ style: { marginBottom: "1rem" } })}
      <div className="alert alert-info">
        <i className="bi bi-hourglass-split me-2" />
        Závod {race.name ? <strong>{race.name}</strong> : null} zatím není spuštěný.
      </div>
      <div className="text-muted small mb-3">
        Obrazovka rozhodčího se otevře sama, jakmile správce závod spustí – stránku stačí nechat otevřenou.
      </div>
      <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>← Zpět</button>
    </div>
  );

  if (myStations.length === 0) return (
    <div className="container mt-5 text-center">
      {outboxNotice({ style: { marginBottom: "1rem" } })}
      {/* Jediné stanoviště rozhodčího smazali v nastavení */}
      {stationNotice && <div className="alert alert-warning small">{stationNotice}</div>}
      <div className="alert alert-warning">
        <i className="bi bi-person-x me-2" />
        Nejste přiřazeni jako rozhodčí na žádné stanoviště tohoto závodu.
      </div>
      <div className="text-muted small">{race?.name}</div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────────────────

  // Stav připojení pro odznak wifi (D1, D2)
  const wifiTitle =
    (!isOnline
      ? "Offline – zkontrolujte připojení"
      : weakSignal
      ? "Slabé spojení – výsledky se odešlou, až bude signál"
      : "Připojeno – výsledky se ukládají online") +
    (unsentCount > 0 ? ` · Neodesláno: ${unsentCount}` : "");
  // Nástroje navíc v horní liště (Ukončit, opravy, zveřejnění)
  const hasBarTools = canFinishRace || canCorrect || canToggleLivePublic;
  const barTools = (
    <>
      {/* Ukončit závod – jen hlavní rozhodčí / super admin */}
      {canFinishRace && (
        <button
          className="btn btn-sm btn-warning text-dark"
          onClick={handleFinishRace}
          disabled={finishing}
          title="Ukončit závod"
        >
          {finishing
            ? <span className="spinner-border spinner-border-sm" />
            : <><i className="bi bi-flag-fill me-1" />Ukončit</>
          }
        </button>
      )}

      {/* Online/offline indikátor; slabé spojení (D2) a počet neodeslaných výsledků (D1) */}
      <span
        className={`badge ${!isOnline ? "bg-danger" : weakSignal ? "bg-warning text-dark" : "bg-success"}`}
        title={wifiTitle}
        aria-label={wifiTitle}
        style={{ cursor: "default", fontSize: "0.85rem", padding: "0.35rem 0.45rem" }}
      >
        {/* Slabé spojení = běžná ikona na žluté (bi-wifi-1 je jen tečka) */}
        <i className={`bi bi-wifi${!isOnline ? "-off" : ""}`} />
        {unsentCount > 0 && <span className="ms-1" aria-label={`Neodesláno: ${unsentCount}`}>{unsentCount}</span>}
      </span>
      <a
        href={`/display/${raceId}`}
        target="_blank"
        rel="noreferrer"
        className="btn btn-sm btn-outline-light"
        title="Otevřít výsledkovou tabuli"
      >
        <i className="bi bi-tv" />
      </a>
      {canCorrect && (
        <button
          className="btn btn-sm btn-outline-warning"
          onClick={() => navigate(`/admin/race-results/${raceId}`)}
          title="Opravy výsledků a export"
        >
          <i className="bi bi-pencil-square" />
        </button>
      )}
      {canToggleLivePublic && (
        <button
          className={`btn btn-sm ${race?.livePublic === false ? "btn-danger" : "btn-outline-light"}`}
          title={race?.livePublic === false ? "Výsledky skryty – kliknutím zveřejnit" : "Výsledky jsou veřejné – kliknutím skrýt"}
          onClick={() => {
            if (!raceId) return;
            const next = race?.livePublic === false ? true : false;
            // Nečeká na server; zamítnutí (jiný stav závodu) jen hláškou (D5)
            updateDoc(doc(db, "races", raceId), { livePublic: next }).catch(err => {
              reportWarning("JudgeView.livePublic", err, { raceId });
              showToast(`Zveřejnění výsledků se nepodařilo změnit: ${firebaseErrorMessage(err)}`, "error");
            });
          }}
        >
          <i className={`bi bi-eye${race?.livePublic === false ? "-slash" : ""}`} />
        </button>
      )}
    </>
  );

  // ── Automatická rotace: přehled, čekací obrazovka, uvolnění ──────────
  // Přehled „Kde jsou rundy“ (hlavní rozhodčí a správce) – na výběru
  // stanoviště, čekací obrazovce a ve výběru střelce, nikdy u gongů
  const rotationOverview = autoMode && rotationState && isStaffOrHeadJudge ? (
    <RotationOverview
      state={rotationState}
      stations={allStations}
      results={liveResults}
      canUndo={isStaffOrHeadJudge}
      onUndo={(stationId, roundNumber) => writeRelease(stationId, roundNumber, false)}
    />
  ) : null;
  /** Číslo stanoviště podle id (čekací obrazovka). */
  const stationNumberOf = (id: string): string => {
    const st = allStations.find(x => x.id === id);
    return st ? String(st.number) : id;
  };
  // „Runda hotová – chybí #N“: jen čekaní bez výsledku (ne mimo pořadí);
  // rezervovaného druhým rozhodčím nejde vynechat; runda tu už střílí,
  // zbývá jediný, nebo je na telefonu potvrzená
  const releaseMissing: RoundShooter[] | null = (() => {
    if (!autoMode || !autoStop || autoStop.released || !canWriteRaceAux || !currentRound || !currentStation) return null;
    const unscored = gongs.available.filter(s => !withdrawnHere.has(s.startNumber));
    if (unscored.length === 0) return null;
    if (isDualStation && unscored.some(s => partnerPickedSNs.has(s.startNumber))) return null;
    if (!(autoStop.touched || unscored.length === 1 || manualRoundNumber !== null)) return null;
    return unscored;
  })();
  // Navržená runda zatím nepotvrzená (výběr rundy „automaticky“, nápověda)
  const suggestionPending = autoMode && manualRoundNumber === null && lockRoundHere === null && roundShownNo !== null;

  return (
    // Výška z rodiče (Bare – flex sloupec s bezpečnými okraji), ne z jednotek
    // okna: 100dvh Chrome 80 nezná (ZÁSAH/NULA by nevyplnily obrazovku)
    // a 100vh i 100dvh přetékají přes výřez iPhonu (I6, návrh 5.6)
    <div style={{
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "hidden",
      backgroundColor: "#f8f9fa",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* ── Top bar ── */}
      <div
        ref={topBarRef}
        className="lks-judge-hgap"
        style={{
          background: "#004d40",
          color: "#fff",
          padding: "0.75rem 1rem",
          display: "flex",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <button
          className="btn btn-sm btn-outline-light"
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left" />
        </button>
        <div className="flex-grow-1">
          <div className="fw-bold" style={{ fontSize: "0.95rem" }}>{race?.name}</div>
          {currentStation && (
            <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
              St. {currentStation.number}: {currentStation.name}
            </div>
          )}
        </div>

        {/* Progress % – uložené výsledky stanovišť / očekávané (I8) */}
        {progressExpected > 0 && (
          <div
            style={{ fontSize: "0.75rem", textAlign: "right", lineHeight: 1.3 }}
            title={`Uložené výsledky stanovišť: ${progressDone} z ${progressExpected}`}
          >
            <div className="fw-bold">{progressPct}%</div>
            <div style={{ opacity: 0.7 }}>{progressDone}/{progressExpected}</div>
          </div>
        )}

        {/* Nástroje vpravo. Správce a hlavní rozhodčí jich mají víc – na úzkém
            telefonu by přetekly mimo obrazovku, proto jsou ve skupině, která se
            zalomí na druhý řádek téže lišty (I6/V1). Lišta rozhodčího stanoviště
            (bez nástrojů navíc) je beze změny. */}
        {hasBarTools ? <div className="lks-judge-tools">{barTools}</div> : barTools}
      </div>

      {/* ── Neuložené výsledky (schránka) – pod lištou, nepřekrývá ji ── */}
      {outboxNotice({ band: true, shownGongKey: gongs.shownKey })}

      {/* ── Nová verze aplikace (I9): jen mimo rozpracovaného střelce, obnoví rozhodčí sám ── */}
      {newAppVersion && !scoringLock && (
        <div className="alert alert-info small py-1 px-3 mb-0 rounded-0 border-0 d-flex align-items-center" role="status" style={{ flexShrink: 0 }}>
          <i className="bi bi-arrow-clockwise me-2" />
          <span className="flex-grow-1">Je nová verze aplikace – obnovte ji.</span>
          <button type="button" className="btn btn-sm btn-outline-primary py-0 ms-2" onClick={() => window.location.reload()}>
            Obnovit
          </button>
        </div>
      )}

      {/* ── Varování: offline ── */}
      {!isOnline && (
        <div style={{
          background: "#e65100",
          color: "#fff",
          padding: "0.4rem 1rem",
          fontSize: "0.82rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexShrink: 0,
        }}>
          <i className="bi bi-wifi-off" />
          <span><strong>Offline</strong> – výsledky se uloží automaticky po obnovení připojení.</span>
        </div>
      )}

      {/* ── Varování: výsledky skryté ── */}
      {race?.livePublic === false && (
        <div style={{
          background: "#c62828",
          color: "#fff",
          padding: "0.35rem 1rem",
          fontSize: "0.82rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexShrink: 0,
        }}>
          <i className="bi bi-eye-slash-fill" />
          {canToggleLivePublic ? (
            <span>
              <strong>Výsledky jsou skryté</strong> – uživatelé je nevidí.
              Klikni na tlačítko <i className="bi bi-eye-slash" /> pro zveřejnění.
            </span>
          ) : (
            <span><strong>Výsledky jsou skryté</strong> – výsledky se zobrazí po rozhodnutí organizátora.</span>
          )}
        </div>
      )}

      {/* ── Vybrané stanoviště zmizelo (smazané v nastavení) ── */}
      {stationNotice && (
        <div className="alert alert-warning small py-2 px-3 mb-0 rounded-0 border-0 d-flex align-items-center" role="status" style={{ flexShrink: 0 }}>
          <i className="bi bi-exclamation-triangle-fill me-2" />
          <span className="flex-grow-1">{stationNotice}</span>
          <button type="button" className="btn-close ms-2" aria-label="Zavřít" onClick={() => setStationNotice(null)} />
        </div>
      )}

      {/* ── Výběr stanoviště + rundy – dropdowny ── */}
      {(myStations.length >= 1 || (race !== null && currentStation && currentStation.type !== "target" && rounds.length > 0)) && (() => {
        // Na PC: pokud jsou vybrány obě hodnoty → kompaktní řádek vedle sebe, zamčeno
        const bothSelected = !!selectedStationId && manualRoundNumber !== null;
        // Úzký telefon (3b – na 360 px se výběry ořezávaly na „1. Gor“,
        // „— Vybert“, „Runda“): při výběru je každý výběr na vlastním řádku
        // přes celou šířku; při bodování (obě hodnoty vybrané a zamčené) bez
        // viditelných popisků (celou šířku položky má výběr) a runda jen
        // „Runda N“ (stav je v souhrnu). Položky, jejich šířky a zalomení
        // řádků při bodování jsou přesně jako dřív (flex 1 1 160px, souhrn
        // 0 0 auto) – výška bloku nad ZÁSAH/NULA se při žádné šířce nemění
        // (Nesmí se změnit).
        const compact = isMobile && bothSelected;
        const stacked = isMobile && !bothSelected;
        const itemFlex = stacked ? "1 1 100%" : isMobile ? "1 1 160px" : "none";
        const labelClass = compact
          ? "visually-hidden"
          : "form-label small fw-semibold mb-0 text-muted text-nowrap me-2";
        const labelStyle = stacked ? { minWidth: "5.25rem" } : undefined;
        return (
        <div
          className="px-3 pt-2 pb-1"
          style={{
            maxWidth: bothSelected ? undefined : (isMobile ? undefined : 480),
            margin: bothSelected ? undefined : (isMobile ? undefined : "0 auto"),
          }}
        >
        {/* Mezery okraji (.lks-judge-selects), ne flex gap – Chrome 80 */}
        <div
          className="lks-judge-selects"
          style={{ flexDirection: bothSelected ? "row" : (isMobile ? "row" : "column") }}
        >
          {/* Dropdown: Stanoviště – vždy pokud existuje aspoň jedno */}
          {myStations.length >= 1 && (
            <div style={{ flex: itemFlex, minWidth: 0 }}>
              <div className="d-flex align-items-center">
                <label className={labelClass} style={labelStyle} htmlFor="lks-judge-station">Stanoviště</label>
                <select
                  id="lks-judge-station"
                  className="form-select form-select-sm flex-grow-1"
                  style={{ minWidth: 0 }}
                  value={selectedStationId || ""}
                  onChange={e => {
                    // Jiné stanoviště = nový výběr (rozpracovaný střelec zůstane v konceptu)
                    setSelectedStationId(e.target.value);
                    setPickedShooterStartNum(null);
                    setScoringLock(null);
                    setStationNotice(null);
                    // Automatická rotace: potvrzená runda patří ke stanovišti
                    if (autoMode) { setManualRoundNumber(null); setManualShooterIndex(0); }
                  }}
                  disabled={bothSelected}
                >
                  {!selectedStationId && (
                    <option value="">— Vyberte stanoviště —</option>
                  )}
                  {ownStations.map(stationOption)}
                  {/* Rozhodčí stanoviště může zastoupit kohokoli (rozhodnutí 1, B6) */}
                  {coverStations.length > 0 && (
                    <optgroup label="Zastoupit jiné stanoviště">
                      {coverStations.map(stationOption)}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>
          )}

          {/* Dropdown: Runda */}
          {/* Výběr rundy na každém stanovišti gongů s rundami – i v automatické
              rotaci (návrh je v něm předvybraný, volba platí jen pro tento telefon) */}
          {race !== null && currentStation && currentStation.type !== "target" && rounds.length > 0 && (() => {
            const availableRounds = rounds.filter(r => {
              const active = r.shooters.filter(s => s.active);
              const done = active.filter(s =>
                hasStationResult(liveResults, s.startNumber, currentStation!.id)
              ).length;
              return active.length === 0 || done < active.length;
            });
            // Navržená, ještě nepotvrzená runda (automatická rotace)
            const suggestionShown = autoMode && manualRoundNumber === null && lockRoundHere === null ? roundShownNo : null;

            // Zamknutí: jakmile začala střelba v aktuální rundě
            // = posunuli jsme se za startovací index NEBO je rozpracovaný draft
            const startIdx = (() => {
              if (manualRoundNumber === null) return 0;
              const r = rounds.find(r => r.number === manualRoundNumber);
              const active = r?.shooters.filter(s => s.active) || [];
              const fi = active.findIndex(s =>
                liveResults[s.startNumber]?.stations?.[currentStation!.id] === undefined
              );
              return fi >= 0 ? fi : 0;
            })();
            const hasDraftCurrentShooter = currentShooter
              ? (draftFor(`${currentStation!.id}_${currentShooter.startNumber}`)?.length ?? 0) > 0
              : false;
            const roundLocked = manualRoundNumber !== null &&
              !canCorrect && // hl. rozhodčí a správce NIKDY nezamkneme
              (manualShooterIndex > startIdx || hasDraftCurrentShooter);

            return (
              <div style={{ flex: itemFlex, minWidth: 0 }}>
                <div className="d-flex align-items-center">
                  <label className={labelClass} style={labelStyle} htmlFor="lks-judge-round">
                    Runda
                    {roundLocked && (
                      <span className="text-warning ms-1" title="Zamčeno – probíhá střelba">
                        <i className="bi bi-lock-fill" style={{ fontSize: "0.75rem" }} />
                      </span>
                    )}
                  </label>
                  {/* Popisek je při bodování na telefonu skrytý – zámek zůstane vidět */}
                  {compact && roundLocked && (
                    <span className="text-warning me-1" title="Zamčeno – probíhá střelba">
                      <i className="bi bi-lock-fill" style={{ fontSize: "0.75rem" }} />
                    </span>
                  )}
                  <select
                    id="lks-judge-round"
                    className="form-select form-select-sm flex-grow-1"
                    style={{ minWidth: 0 }}
                    value={(autoMode ? roundShownNo : manualRoundNumber) ?? ""}
                    disabled={roundLocked || bothSelected}
                    onChange={e => {
                    const val = e.target.value;
                    // Střelec vybraný v předchozí rundě se nepřenáší (mohl
                    // mezitím dostat výsledek – obrazovka by na něm uvízla)
                    setPickedShooterStartNum(null);
                    setScoringLock(null);
                    if (!val) { setManualRoundNumber(null); setManualShooterIndex(0); return; }
                    const rNum = parseInt(val);
                    const r = rounds.find(r => r.number === rNum);
                    const active = r?.shooters.filter(s => s.active) || [];
                    const firstIdx = active.findIndex(s =>
                      !hasStationResult(liveResults, s.startNumber, currentStation!.id)
                    );
                    const si = firstIdx >= 0 ? firstIdx : 0;
                    // Jen tento telefon (i v automatické rotaci – celý závod
                    // přepne na ruční rundy jen správce v nastavení, otázka 10)
                    setManualRoundNumber(rNum);
                    setManualShooterIndex(si);
                  }}
                >
                  <option value="">— Vyberte rundu —</option>
                  {availableRounds.map(r => {
                    const active = r.shooters.filter(s => s.active);
                    const done = active.filter(s =>
                      hasStationResult(liveResults, s.startNumber, currentStation!.id)
                    ).length;
                    return (
                      <option key={r.number} value={r.number}>
                        Runda {r.number}{compact ? "" : r.number === suggestionShown ? " (automaticky)" : done > 0 ? ` (${done}/${active.length} hotovo)` : ` (${active.length} střelců)`}
                      </option>
                    );
                  })}
                </select>
                </div> {/* konec d-flex label+select */}
                {manualRoundNumber !== null && !bothSelected && roundSummary(false, roundLocked)}
              </div>
            );
          })()}

          {/* Info + Zrušit – na jednom řádku s dropdowny když bothSelected */}
          {bothSelected && manualRoundNumber !== null && roundSummary(true, false)}
        </div>
        </div>
        );
      })()}

      {currentStation?.type === "target" && (
        <JudgeTarget
          raceId={raceId!}
          station={currentStation}
          rounds={rounds}
          liveResults={liveResults}
          judgeUid={uid}
          onSaveRejected={onSaveRejected}
          onSaveSucceeded={outboxSettled}
          autoRotation={autoMode && rotationState ? {
            suggested,
            isSentOn: (roundNumber: number) => {
              const stop = stopOf(rotationState, roundNumber, currentStation.id);
              return !stop || stop.released || stop.done;
            },
            canRelease: canWriteRaceAux,
            onRelease: (roundNumber: number) => { void confirmReleaseTarget(roundNumber, currentStation.id); },
          } : null}
        />
      )}

      {/* ── Notifikace: partner převzal tvého střelce ── */}
      {takeoverNotif && (
        <div style={{
          position: "fixed", bottom: "calc(env(safe-area-inset-bottom) + 5rem)",
          left: "50%", transform: "translateX(-50%)",
          background: "#e65100", color: "#fff",
          padding: "0.75rem 1.25rem", borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
          zIndex: 9999, maxWidth: "90vw", textAlign: "center",
          fontSize: "0.88rem", fontWeight: 500,
          display: "flex", alignItems: "center", gap: "0.75rem",
        }}>
          <i className="bi bi-person-fill-x" style={{ fontSize: "1.2rem" }} />
          <div>
            <div style={{ fontWeight: 700 }}>Střelec převzat</div>
            <div>
              <strong>#{takeoverNotif.sn} {takeoverNotif.name}</strong> byl převzat{" "}
              {takeoverNotif.by
                ? <>rozhodčím <strong>{takeoverNotif.by}</strong></>
                : "druhým rozhodčím"}
            </div>
          </div>
          <button className="btn btn-sm btn-light ms-1 py-0 px-2" onClick={() => setTakeoverNotif(null)}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}

      {/* ── Gongy – sekvenční / výběr – vyplní zbývající prostor ── */}
      {(currentStation?.type === "gongs_sequential" || currentStation?.type === "gongs_choice") && (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Dual bez spojení (rozhodnutí 7′): rezervace čeká ve frontě */}
          {isDualStation && dualOffline && (
            <div className="alert alert-warning small py-1 px-3 mb-0 rounded-0 border-0" role="status" style={{ flexShrink: 0 }}>
              <i className="bi bi-wifi-off me-1" />
              {dualPicks.DUAL_OFFLINE_WARNING}
            </div>
          )}

          {/* ══════════════════════════════════════════════
              SHOOTER PICKER – zobrazí se pokud není vybrán střelec
          ══════════════════════════════════════════════ */}
          {gongs.roundShown && (() => {
            // Aktivní a dostupní střelci a koho gongy ukazují – výpočet výše
            const { active, available, effectiveAvailable, shooterToPick } = gongs;

            if (gongs.visible && shooterToPick) {
              // Zobraz shooting UI – nastavení stanoviště zmrazené pro tohoto
              // střelce; každý střelec má vlastní gongy (key): pozdní výsledek
              // uložení předchozího střelce už obrazovku nezmění
              const GongsComponent = gongsStation!.type === "gongs_sequential" ? JudgeGongsSequential : JudgeGongsChoice;
              // Pořadí v rundě (rozpracovaný „nenastoupil“ už mezi aktivními není)
              const activeIdx = active.findIndex(s => s.startNumber === shooterToPick.startNumber);
              const shooterIdx = activeIdx >= 0
                ? activeIdx
                : Math.max(0, (currentRound?.shooters || []).findIndex(s => s.startNumber === shooterToPick.startNumber));
              const isDual = isDualStation;
              const stId = gongsStation!.id;

              return (
                <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {/* Dual info pruh */}
                  {isDual && (
                    <div className="lks-judge-hgap" style={{ background: "#1b5e20", color: "#fff",
                      padding: "0.35rem 1rem", fontSize: "0.8rem", display: "flex", alignItems: "center",
                      flexShrink: 0 }}>
                      <i className="bi bi-bullseye" />
                      <span style={{ marginRight: "0.75rem" }}>Dual mód · Váš střelec: <strong>#{shooterToPick.startNumber} {shooterToPick.name}</strong></span>
                      <button className="btn btn-sm btn-outline-light ms-auto py-0 px-2"
                        style={{ fontSize: "0.72rem" }}
                        onClick={() => { setPickedShooterStartNum(null); setScoringLock(null); releaseDualPick(); }}>
                        Uvolnit
                      </button>
                    </div>
                  )}
                  {/* Nastavení stanoviště se změnilo – rozpracovaný střelec doboduje podle původního */}
                  {/* Jeden řádek i na 360 px – ZÁSAH/NULA o místo nepřijdou */}
                  {stationConfigChanged && (
                    <div className="small text-muted text-center text-truncate px-3 pt-1" style={{ flexShrink: 0 }}>
                      <i className="bi bi-info-circle me-1" />
                      Nové nastavení platí od dalšího střelce.
                    </div>
                  )}
                  <GongsComponent
                    key={gongs.shownKey || undefined}
                    station={gongsStation!}
                    currentRound={currentRound}
                    currentShooter={shooterToPick}
                    currentShooterIndex={shooterIdx}
                    onResultSaved={async (result) => {
                      // Koncept se nemaže předem – smaže ho až přijatý výsledek
                      // (stejná dávka); zamítnutí jde dál ke gongům (hláška)
                      if (isDual) {
                        await handleDualResultSaved(result, shooterToPick);
                      } else {
                        await handleResultSaved(result, shooterToPick);  // FIX: předej skutečně vybraného střelce
                      }
                    }}
                    draftShots={draftFor(`${stId}_${shooterToPick.startNumber}`)}
                    onDraftChange={(shots) => saveDraft(stId, shooterToPick.startNumber, shots)}
                    existingResult={liveResults[shooterToPick.startNumber]?.stations?.[stId]}
                    saveError={saveErrors[`${stId}_${shooterToPick.startNumber}`]}
                  />
                </div>
              );
            }

            // Runda hotová
            if (gongs.roundDone) {
              return (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", padding: "2rem", gap: "1rem" }}>
                  <div style={{ fontSize: "2rem" }}>✅</div>
                  <h5 className="fw-bold">Runda {currentRound?.number} hotová</h5>
                  <p className="text-muted small text-center">Všichni střelci na tomto stanovišti mají výsledky.</p>
                  {isDualStation && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={releaseDualPick}>
                      <i className="bi bi-arrow-repeat me-1" />Změnit roli
                    </button>
                  )}
                </div>
              );
            }

            // DUAL: čekám – zbývá 1 střelec a partner ho má
            if (isDualStation && isDualWaiting) {
              const stId = currentStation!.id;
              const last = active.find(s =>
                liveResults[s.startNumber]?.stations?.[stId] === undefined &&
                partnerPickedSNs.has(s.startNumber)
              );
              // Rozepsané rány partnera (koncept – výsledek ještě nemá)
              const shotsDone = last ? (draftFor(`${stId}_${last.startNumber}`)?.length ?? 0) : 0;
              const shotsTotal = currentStation!.shotsCount ?? 0;
              return (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", padding: "2rem", gap: "1rem" }}>
                  <div className="spinner-border text-success" />
                  <h6 className="fw-bold mb-0">Čekám na posledního střelce</h6>
                  <p className="text-muted small text-center">
                    {last
                      ? <>Druhý rozhodčí zadává <strong>#{last.startNumber} {last.name}</strong>.<br />
                        Po jeho dokončení přejdete na další rundu.</>
                      : "Čekám na dokončení rundy."}
                  </p>
                  {/* Opravit vlastního posledního střelce */}
                  {dualLastEnteredStartNum && (
                    <button className="btn btn-sm btn-outline-warning" onClick={handleDualCorrectLast}>
                      <i className="bi bi-arrow-counterclockwise me-1" />Opravit #{dualLastEnteredStartNum}
                    </button>
                  )}
                  {/* Převzít posledního střelce od partnera (např. technický problém) */}
                  {last && (
                    <div style={{ marginTop: "0.5rem", width: "100%", maxWidth: 340 }}>
                      <button
                        className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                        style={{
                          background: "#fff3cd", border: "1.5px solid #ffc107",
                          color: "#664d03", padding: "0.75rem 1rem", position: "relative",
                        }}
                        disabled={dualBusySN !== null}
                        onClick={() => { void confirmTakeover(last.startNumber, last.name, shotsDone, shotsTotal); }}
                      >
                        <i className="bi bi-lock-fill text-warning" />
                        <span style={{ fontWeight: 700 }}>Převzít #{last.startNumber} {last.name} od druhého rozhodčího</span>
                        {shotsDone > 0 && (
                          <span className="ms-auto small" style={{ color: "#856404", fontWeight: 600 }}>
                            {shotsDone}/{shotsTotal} ran
                          </span>
                        )}
                        {dualBusySpinner(last.startNumber)}
                      </button>
                    </div>
                  )}
                </div>
              );
            }

            // Picker – zobraz seznam dostupných střelců
            // Pro dual: partneři jsou identifikováni přes partnerPickedSNs

            return (
              <div style={{ flex: 1, overflow: "auto", padding: "1rem" }}>
                <div className="text-muted small mb-2 text-center">
                  <i className="bi bi-person-check me-1" />
                  {isDualStation && <span>Dual mód · </span>}
                  Kdo bude střílet?
                </div>
                {suggestionPending && (
                  <div className="text-muted small mb-2 text-center" role="note">
                    Runda navržená podle výsledků – potvrďte ji ťuknutím na prvního střelce. Stojí tu jiná runda? Vyberte ji výše.
                  </div>
                )}

                <div className="d-flex flex-column lks-judge-vgap">
                  {effectiveAvailable.map(s => (
                    // Střelec = velké tlačítko přes celou šířku; nováček je
                    // samostatná malá volba pod ním (I5 – dřív vnořená tlačítka
                    // uvnitř, ťuknutí trefilo jiné, než chtěl rozhodčí)
                    <div key={s.startNumber} className="d-flex flex-column">
                    <button
                      className="btn btn-outline-primary d-flex align-items-center"
                      style={{ textAlign: "left", padding: "0.75rem 1rem", position: "relative" }}
                      disabled={isDualStation && dualBusySN !== null}
                      onClick={() => {
                        // Automatická rotace: první ťuknutí rundu na tomto telefonu potvrdí
                        if (currentRound) adoptRound(currentRound.number);
                        if (isDualStation) {
                          pickDualShooter(s.startNumber);
                          setPickedShooterStartNum(s.startNumber);
                        } else {
                          setPickedShooterStartNum(s.startNumber);
                        }
                      }}>
                      {/* Mezery okraji, ne flex gap (Chrome 80 – I6) */}
                      <div className="d-flex align-items-center flex-grow-1">
                        <span className="me-2" style={{ fontWeight: 700, fontSize: "1.1rem", minWidth: 60 }}>#{s.startNumber}</span>
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center">
                            <span>{s.name}</span>
                            {/* Odznak N: tmavý text na oranžové (I7, kontrast 6,9:1) */}
                            {s.isNewbie && (
                              <span className="badge lks-newbie-badge ms-1" style={{ fontSize: "0.72rem" }}>N</span>
                            )}
                            {s.newbieDisabled && (
                              <span className="badge bg-secondary ms-1" style={{ fontSize: "0.65rem" }} title="Nováček zakázán"><i className="bi bi-ban" /></span>
                            )}
                          </div>
                        </div>
                      </div>
                      {s.sequenceNumber && <span className="text-muted ms-auto small" style={{flexShrink:0}}>LKS #{s.sequenceNumber}</span>}
                      {dualBusySpinner(s.startNumber)}
                    </button>
                    {/* Nováček – jen hlavní rozhodčí a správce (B7: trvalý zákaz
                        mění profil střelce, jen správce; canDisableNewbie
                        rozhodne podle role profilu) */}
                    {canManageNewbie && !s.newbieDisabled && isDualStation === false && (
                      <div className="d-flex justify-content-end align-items-center mt-1 lks-judge-hgap-sm">
                        {s.isNewbie ? (
                          <>
                            <button type="button" className="btn btn-sm lks-newbie-btn lks-newbie-btn--outline"
                              aria-label={`Odebrat nováčka – #${s.startNumber} ${s.name}`}
                              onClick={() => setShooterNewbie(s, false)}>
                              − Odebrat N
                            </button>
                            {isStaff && (
                              <button type="button" className="btn btn-sm btn-outline-danger lks-newbie-btn lks-newbie-btn--ban"
                                onClick={() => { void disableNewbie(s); }}
                                title="Zakázat trvale"
                                aria-label={`Zakázat nováčka trvale – #${s.startNumber} ${s.name}`}>
                                <i className="bi bi-ban me-1" />Zakázat trvale
                              </button>
                            )}
                          </>
                        ) : (
                          <button type="button" className="btn btn-sm lks-newbie-btn lks-newbie-btn--fill"
                            aria-label={`Označit jako nováčka – #${s.startNumber} ${s.name}`}
                            onClick={() => setShooterNewbie(s, true)}>
                            + Nováček
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                  ))}
                </div>

                {/* ── Automatická rotace: mimo pořadí (chyběli dřív) – jde bodovat, sám se nevybere ── */}
                {autoMode && gongs.offRoster.length > 0 && (() => {
                  const open = showOffRoster || effectiveAvailable.length === 0;
                  return (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-muted text-decoration-none"
                        aria-expanded={open}
                        onClick={() => setShowOffRoster(v => !v)}
                      >
                        <i className={`bi bi-chevron-${open ? "down" : "right"} me-1`} />
                        Mimo pořadí ({gongs.offRoster.length}) – chyběli na dřívějším stanovišti
                      </button>
                      {open && (
                        <div className="d-flex flex-column lks-judge-vgap mt-2">
                          {gongs.offRoster.map(s => (
                            <button
                              key={s.startNumber}
                              className="btn btn-outline-secondary d-flex align-items-center"
                              style={{ textAlign: "left", padding: "0.75rem 1rem", position: "relative" }}
                              disabled={isDualStation && dualBusySN !== null}
                              onClick={() => {
                                if (currentRound) adoptRound(currentRound.number);
                                if (isDualStation) pickDualShooter(s.startNumber);
                                setPickedShooterStartNum(s.startNumber);
                              }}
                            >
                              <span className="me-2" style={{ fontWeight: 700, fontSize: "1.1rem", minWidth: 60 }}>#{s.startNumber}</span>
                              <span>{s.name}</span>
                              {dualBusySpinner(s.startNumber)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Automatická rotace: střelec chybí / odstoupil → runda jde dál ── */}
                {releaseMissing && currentRound && currentStation && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => { void confirmReleaseMissing(currentRound.number, currentStation.id, releaseMissing); }}
                    >
                      <i className="bi bi-flag me-1" />
                      Runda hotová – chybí {releaseMissing.map(x => `#${x.startNumber}`).join(", ")}
                    </button>
                  </div>
                )}

                {/* ── Střelci u partnera – možnost převzetí ── */}
                {isDualStation && partnerPickedSNs.size > 0 && (() => {
                  const partnerShooters = available.filter(s => partnerPickedSNs.has(s.startNumber));
                  if (partnerShooters.length === 0) return null;
                  return (
                    <div className="mt-3">
                      <div className="text-muted small mb-2 d-flex align-items-center gap-1">
                        <i className="bi bi-exclamation-triangle-fill text-warning" />
                        Locknutí u druhého rozhodčího – lze převzít
                      </div>
                      <div className="d-flex flex-column lks-judge-vgap">
                        {partnerShooters.map(s => {
                          const stId = currentStation!.id;
                          // Rozepsané rány partnera (koncept – výsledek ještě nemá)
                          const shotsDone = draftFor(`${stId}_${s.startNumber}`)?.length ?? 0;
                          const shotsTotal = currentStation!.shotsCount ?? 0;
                          return (
                            <button key={s.startNumber}
                              className="btn d-flex align-items-center"
                              style={{
                                textAlign: "left", padding: "0.75rem 1rem",
                                background: "#fff3cd", border: "1.5px solid #ffc107",
                                color: "#664d03", position: "relative",
                              }}
                              disabled={dualBusySN !== null}
                              onClick={() => { void confirmTakeover(s.startNumber, s.name, shotsDone, shotsTotal); }}>
                              <i className="bi bi-lock-fill me-2 text-warning" />
                              <span style={{ fontWeight: 700, fontSize: "1.1rem", minWidth: 60 }}>#{s.startNumber}</span>
                              <span>{s.name}</span>
                              {shotsDone > 0 && (
                                <span className="ms-auto small" style={{ color: "#856404", fontWeight: 600 }}>
                                  {shotsDone}/{shotsTotal} ran
                                </span>
                              )}
                              {s.sequenceNumber && shotsDone === 0 && (
                                <span className="text-muted ms-auto small">LKS #{s.sequenceNumber}</span>
                              )}
                              {dualBusySpinner(s.startNumber)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Přehled rund (hlavní rozhodčí) – jen před potvrzením rundy */}
                {manualRoundNumber === null && rotationOverview}
              </div>
            );
          })()}

          {/* ── Automatická rotace: na stanovišti teď žádná runda (čekání) ── */}
          {autoMode && rotationState && !gongs.roundShown && rounds.length > 0 && currentStation && (() => {
            const outside = !rotationState.frozen.stations.includes(currentStation.id);
            const next = outside ? null : upcomingRounds(rotationState, currentStation.id)[0] || null;
            const nothingLeft = !outside && !next && stationQueue(rotationState, currentStation.id).length === 0;
            return (
              <div style={{ flex: 1, overflow: "auto", padding: "1rem" }}>
                <div className="card shadow-sm mx-auto" style={{ maxWidth: 420 }}>
                  <div className="card-body text-center py-4">
                    <i className={`bi ${nothingLeft ? "bi-check2-circle text-success" : "bi-hourglass-split text-warning"} fs-1 d-block mb-2`} />
                    {outside ? (
                      <h6 className="fw-bold mb-0">Stanoviště není v automatické rotaci – vyberte rundu výše.</h6>
                    ) : next ? (
                      <h6 className="fw-bold mb-0">
                        Další runda: Runda {next.round} – {next.firstThere
                          ? `teď na stanovišti ${stationNumberOf(next.atStationId)} (${next.doneCount}/${next.expectedCount})`
                          : `čeká na uvolnění stanoviště ${stationNumberOf(next.atStationId)}`}
                      </h6>
                    ) : (
                      <h6 className="fw-bold mb-0">Na tomto stanovišti mají všechny rundy hotovo.</h6>
                    )}
                    <p className="text-muted small mb-0 mt-2">
                      Stojí tu jiná runda? Vyberte ji – předchozí stanoviště možná nemá signál.
                    </p>
                  </div>
                </div>
                {rotationOverview}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Žádné stanoviště vybráno ── */}
      {!currentStation && myStations.length > 0 && (
        <div className="container mt-4 text-center text-muted">
          <i className="bi bi-cursor fs-1 d-block mb-2" />
          Vyberte stanoviště výše
          {rotationOverview}
        </div>
      )}

      {/* ── Čekací stav – žádná runda, žádné rundy vůbec v závodě (automatická rotace) ── */}
      {currentStation && (currentStation.type === "gongs_sequential" || currentStation.type === "gongs_choice") && !currentRound && autoMode && rounds.length === 0 && (
        <div className="container mt-4 text-center">
          <div className="card shadow-sm mx-auto" style={{ maxWidth: 400 }}>
            <div className="card-body py-5">
              <i className="bi bi-hourglass-split fs-1 text-warning d-block mb-3" />
              <h5>Žádné rundy</h5>
              <p className="text-muted small mb-0">
                Závod ještě nemá přiřazeny rundy. Kontaktujte organizátora.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JudgeView;
